import React, { useState, useEffect, useRef, useMemo, useCallback, useReducer } from "react";
import "cropperjs/dist/cropper.css";
import "./Calender.css";
import axios from "axios";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { toast } from "react-toastify";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Modal from "react-modal";
import { useSelector, useDispatch } from "react-redux";
import { setHeaderText } from "../../features/headerText/headerTextSlice.js";
import "react-tabs/style/react-tabs.css";
import PlusIcon from "../../images/icons/plus-icon.svg";
import ArrowIcon from "../../images/icons/arrow-bottom-white.svg";
import ClockIcon from "../../images/icons/clock.svg";
import cancelImage from "../../images/singleinvoice/cancel.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import { CATEGORYLIST, checkMicrosoftTokenValidity, checkMicrosoftCalendarEnabledStatus, groupEventsByKey, STATUS_COLORS } from "./services.js";
import { debounce } from "lodash";
import SafeHtml from "./CalendarComponents/SafeHtml.jsx";
import { getAppTimezone, toAppTimezone, startOfDayInTimezone } from "./CalendarComponents/timezoneUtils.js";
import { CALENDAR_CONFIG } from "./CalendarComponents/calendarConstants.js";
// Import new components
import { useCalendarEvents } from "./CalendarComponents/useCalendarEvents.js";
import { useCalendarSync } from "./CalendarComponents/useCalendarSync.js";
import { useUserSuggestions } from "../User/userProfileQueries.jsx";
// Import event transformation utilities
import { eventKey, applyOptimistic, filterEvents } from "./CalendarComponents/eventTransformUtils.js";
import { executeOptimisticUpdate, getErrorMessage } from "./CalendarComponents/optimisticUpdates.js";
// Import state reducers
import { eventFormReducer, eventFormActions } from "./CalendarComponents/eventFormReducer.js";
import { uiStateReducer, uiStateActions } from "./CalendarComponents/uiStateReducer.js";
import { loadingStateReducer, loadingStateActions } from "./CalendarComponents/loadingStateReducer.js";
import { filterStateReducer, filterStateActions } from "./CalendarComponents/filterStateReducer.js";
// Import modal components
import ViewCalendarEventModal from "./ViewCalendarEventModal.jsx";
import CreateEventModal from "./CreateEventModal.jsx";
import CreateHolidayModal from "./CreateHolidayModal.jsx";
import GoogleTokenInvalidModal from "./GoogleTokenInvalidModal.jsx";
import MicrosoftTokenInvalidModal from "./MicrosoftTokenInvalidModal.jsx";
import ImportDateRangeModal from "./ImportDateRangeModal.jsx";
// Import wrapper components
import CustomTimeSlotsWrapper from "./CustomTimeSlotsWrapper.jsx";
// Import error handling components
import { MultipleErrorsDisplay } from "./CalendarComponents/ErrorDisplay.jsx";
import { CalendarSkeleton, FilterBarSkeleton } from "./CalendarComponents/SkeletonLoader.jsx";
// Mock constants (replace with actual imports/values)

const SUPER_ADMIN = 1;
const ADMIN = 2;

Modal.setAppElement("#root");

const Calender = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const localizer = momentLocalizer(moment);
    const DnDCalendar = withDragAndDrop(BigCalendar);
    // Grouped state using useReducer
    const [uiState, dispatchUI] = useReducer(uiStateReducer, {
        modalIsOpen: false,
        createEventModalOpen: false,
        createHolidayModalOpen: false,
        isGoogleTokenInvalidModalOpen: false,
        isMicrosoftTokenInvalidModalOpen: false,
        isOpen: false,
        isUserDropdownOpen: false,
        view: 'month',
        filter: '',
        filt: '',
        selectedEvents: [],
        expandedEventIndex: null,
        selectedEventForEdit: null,
    });

    const [eventForm, dispatchEventForm] = useReducer(eventFormReducer, {
        eventName: '',
        eventDescription: '',
        eventLocation: '',
        eventOrganizer: '',
        eventAttendees: [],
        externalAttendees: [],
        eventBannerFile: null,
        eventBanner: null,
        isVirtual: false,
        eventMeetingLink: '',
        eventDate: null,
        eventStartTime: '',
        eventEndTime: '',
        startTime: '',
        endTime: '',
        startTimeString: '',
        endTimeString: '',
        isSlotSelected: false,
        repeatType: 'does_not_repeat',
        reminderMethod: 'popup',
        reminderMinutes: 10,
        eventNameIsValid: false,
        eventDateIsValid: false,
        eventStartTimeIsValid: false,
        eventEndTimeIsValid: false,
        startTimeIsValid: false,
        endTimeIsValid: false,
        repeatTypeIsValid: false,
        reminderMinutesIsValid: false,
        repeatTypeDropdownOpen: false,
        organizerDropdownOpen: false,
        reminderMethodDropdownOpen: false,
    });

    const [loadingState, dispatchLoading] = useReducer(loadingStateReducer, {
        isSubmitLoading: false,
        isUpdating: false,
        isCategoryLoading: false,
        isUserLoading: false,
    });

    // Get user from Redux store first
    const { user } = useSelector((state) => state.auth);
    const token = useSelector((state) => state.auth.token);
    const { appSettings } = useSelector((state) => state.appSettings);

    const [filterState, dispatchFilter] = useReducer(filterStateReducer, {
        selectedUsers: user?._id ? [user._id] : [],
        selectedCategories: CATEGORYLIST.map(c => c.name),
        googleMicrosoftUserSearchTerm: '',
        selectAllUsers: false,
        leaveFilterUsers: user?._id && !(user.userType === SUPER_ADMIN || user.userType === ADMIN) ? [user._id] : [],
        leaveUserSearchTerm: '',
        selectAllLeaveUsers: false,
    });

    const userDropdownRef = useRef(null);
    const leaveUserDropdownRef = useRef(null);
    const isClosingModalRef = useRef(false);
    const createDropdownRef = useRef();
    const importCanceledRef = useRef(false);
    const tz = useMemo(() => getAppTimezone(), []);

    // Helper functions for backward compatibility and easier state updates
    // UI State helpers
    const setModalIsOpen = (value) => dispatchUI({ type: uiStateActions.SET_MODAL, modalName: 'modalIsOpen', isOpen: value });
    const setCreateEventModalOpen = (value) => dispatchUI({ type: uiStateActions.SET_MODAL, modalName: 'createEventModalOpen', isOpen: value });
    const setCreateHolidayModalOpen = (value) => dispatchUI({ type: uiStateActions.SET_MODAL, modalName: 'createHolidayModalOpen', isOpen: value });
    const setSelectedEvents = (value) => dispatchUI({ type: uiStateActions.SET_SELECTED_EVENTS, events: value });
    const setExpandedEventIndex = (value) => dispatchUI({ type: uiStateActions.SET_EXPANDED_INDEX, index: value });
    const setSelectedEventForEdit = (value) => dispatchUI({ type: uiStateActions.SET_SELECTED_EVENT_FOR_EDIT, event: value });
    const setIsUserDropdownOpen = (value) => dispatchUI({ type: uiStateActions.SET_DROPDOWN, dropdownName: 'isUserDropdownOpen', isOpen: value });
    const setIsLeaveUserDropdownOpen = (value) => dispatchUI({ type: uiStateActions.SET_DROPDOWN, dropdownName: 'isLeaveUserDropdownOpen', isOpen: value });
    const setIsOpen = (value) => dispatchUI({ type: uiStateActions.SET_DROPDOWN, dropdownName: 'isOpen', isOpen: value });
    const setIsGoogleTokenInvalidModalOpen = (value) => dispatchUI({ type: uiStateActions.SET_MODAL, modalName: 'isGoogleTokenInvalidModalOpen', isOpen: value });
    const setIsMicrosoftTokenInvalidModalOpen = (value) => dispatchUI({ type: uiStateActions.SET_MODAL, modalName: 'isMicrosoftTokenInvalidModalOpen', isOpen: value });
    const setIsImportDateRangeModalOpen = (value, dateRange) => {
        dispatchUI({ type: uiStateActions.SET_IMPORT_DATE_RANGE_MODAL, isOpen: value, dateRange });
    };
    const setIsSelectionMode = (value) => dispatchUI({ type: uiStateActions.SET_SELECTION_MODE, isSelectionMode: value });
    const setSelectedEventsForImport = (value) => dispatchUI({ type: uiStateActions.SET_SELECTED_EVENTS_FOR_IMPORT, events: value });
    const setSelectAllEvents = (value) => dispatchUI({ type: uiStateActions.SET_SELECT_ALL_EVENTS, selectAll: value });
    const setIsImportLoading = (value) => dispatchUI({ type: uiStateActions.SET_IMPORT_LOADING, isLoading: value });

    // Event Form helpers
    const setEventName = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventName', value });
    const setEventDescription = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventDescription', value });
    const setEventLocation = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventLocation', value });
    const setEventOrganizer = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventOrganizer', value });
    const setEventAttendees = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventAttendees', value });
    const setExternalAttendees = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'externalAttendees', value });
    const setEventBannerFile = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventBannerFile', value });
    const setEventBanner = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventBanner', value });
    const setIsVirtual = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'isVirtual', value });
    const setEventMeetingLink = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventMeetingLink', value });
    const setRepeatType = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'repeatType', value });
    const setEventDate = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventDate', value });
    const setEventStartTime = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventStartTime', value });
    const setEventEndTime = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'eventEndTime', value });
    const setStartTime = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'startTime', value });
    const setEndTime = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'endTime', value });
    const setStartTimeString = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'startTimeString', value });
    const setEndTimeString = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'endTimeString', value });
    const setIsSlotSelected = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'isSlotSelected', value });
    const setReminderMethod = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'reminderMethod', value });
    const setReminderMinutes = (value) => dispatchEventForm({ type: eventFormActions.SET_FIELD, field: 'reminderMinutes', value });
    const setEventNameIsValid = (value) => dispatchEventForm({ type: eventFormActions.SET_VALIDATION, field: 'eventNameIsValid', isValid: value });
    const setEventDateIsValid = (value) => dispatchEventForm({ type: eventFormActions.SET_VALIDATION, field: 'eventDateIsValid', isValid: value });
    const setEventStartTimeIsValid = (value) => dispatchEventForm({ type: eventFormActions.SET_VALIDATION, field: 'eventStartTimeIsValid', isValid: value });
    const setEventEndTimeIsValid = (value) => dispatchEventForm({ type: eventFormActions.SET_VALIDATION, field: 'eventEndTimeIsValid', isValid: value });
    const setStartTimeIsValid = (value) => dispatchEventForm({ type: eventFormActions.SET_VALIDATION, field: 'startTimeIsValid', isValid: value });
    const setEndTimeIsValid = (value) => dispatchEventForm({ type: eventFormActions.SET_VALIDATION, field: 'endTimeIsValid', isValid: value });
    const setRepeatTypeIsValid = (value) => dispatchEventForm({ type: eventFormActions.SET_VALIDATION, field: 'repeatTypeIsValid', isValid: value });
    const setReminderMinutesIsValid = (value) => dispatchEventForm({ type: eventFormActions.SET_VALIDATION, field: 'reminderMinutesIsValid', isValid: value });
    const setRepeatTypeDropdownOpen = (value) => dispatchEventForm({ type: eventFormActions.SET_DROPDOWN_OPEN, field: 'repeatTypeDropdownOpen', isOpen: value });
    const setOrganizerDropdownOpen = (value) => dispatchEventForm({ type: eventFormActions.SET_DROPDOWN_OPEN, field: 'organizerDropdownOpen', isOpen: value });
    const setReminderMethodDropdownOpen = (value) => dispatchEventForm({ type: eventFormActions.SET_DROPDOWN_OPEN, field: 'reminderMethodDropdownOpen', isOpen: value });

    // Loading State helpers
    const setIsSubmitLoading = (value) => dispatchLoading({ type: loadingStateActions.SET_LOADING, field: 'isSubmitLoading', value });
    const setIsUpdating = (value) => dispatchLoading({ type: loadingStateActions.SET_LOADING, field: 'isUpdating', value });
    const setIsUserLoading = (value) => dispatchLoading({ type: loadingStateActions.SET_LOADING, field: 'isUserLoading', value });

    // Filter State helpers
    const setSelectedUsers = (value) => dispatchFilter({ type: filterStateActions.SET_SELECTED_USERS, users: value });
    const setSelectedCategories = (value) => dispatchFilter({ type: filterStateActions.SET_SELECTED_CATEGORIES, categories: value });
    const setGoogleMicrosoftUserSearchTerm = (value) => dispatchFilter({ type: filterStateActions.SET_SEARCH_TERM, term: value });
    const setSelectAllUsers = (value) => dispatchFilter({ type: filterStateActions.SET_SELECT_ALL, value });
    const setLeaveFilterUsers = (value) => dispatchFilter({ type: filterStateActions.SET_LEAVE_FILTER_USERS, users: value });
    const setLeaveUserSearchTerm = (value) => dispatchFilter({ type: filterStateActions.SET_LEAVE_SEARCH_TERM, term: value });
    const setSelectAllLeaveUsers = (value) => dispatchFilter({ type: filterStateActions.SET_SELECT_ALL_LEAVE_USERS, value });

    // Destructure state for easier access
    const {
        modalIsOpen,
        createEventModalOpen,
        createHolidayModalOpen,
        isGoogleTokenInvalidModalOpen,
        isMicrosoftTokenInvalidModalOpen,
        isImportDateRangeModalOpen,
        isOpen,
        isUserDropdownOpen,
        isLeaveUserDropdownOpen,
        view,
        filt,
        selectedEvents,
        expandedEventIndex,
        selectedEventForEdit,
        isSelectionMode,
        selectedEventsForImport = [],
        selectAllEvents = false,
        importDateRange = { startDate: null, endDate: null },
        isImportLoading = false,
    } = uiState;

    const {
        eventName,
        eventDescription,
        eventLocation,
        eventOrganizer,
        eventAttendees,
        externalAttendees,
        eventBannerFile,
        isVirtual,
        eventMeetingLink,
        repeatType,
        eventDate,
        eventStartTime,
        eventEndTime,
        startTimeString,
        endTimeString,
        isSlotSelected,
        reminderMethod,
        reminderMinutes,
        eventNameIsValid,
        eventDateIsValid,
        eventStartTimeIsValid,
        eventEndTimeIsValid,
        startTimeIsValid,
        endTimeIsValid,
        repeatTypeIsValid,
        reminderMinutesIsValid,
        repeatTypeDropdownOpen,
        organizerDropdownOpen,
        reminderMethodDropdownOpen,
    } = eventForm;

    const {
        isSubmitLoading,
        isUpdating,
        isUserLoading,
    } = loadingState;

    const {
        selectedUsers,
        selectedCategories: selectedCategoriesRaw,
        googleMicrosoftUserSearchTerm,
        selectAllUsers,
        leaveFilterUsers,
        leaveUserSearchTerm,
        selectAllLeaveUsers,
    } = filterState;

    // Ensure selectedCategories is always an array to prevent runtime errors
    const selectedCategories = useMemo(() =>
        Array.isArray(selectedCategoriesRaw) ? selectedCategoriesRaw : [],
        [selectedCategoriesRaw]
    );

    const dummyImage = "/Uploads/lowProfileImage/user_default.png";
    const [currentDate, setCurrentDate] = useState(() => {
        // optional persistence across re-renders/page hits with 20-hour expiration
        const savedData = localStorage.getItem('calendar.currentDate');
        if (savedData) {
            try {
                const { date, timestamp } = JSON.parse(savedData);
                const savedDate = new Date(date);
                const savedTimestamp = new Date(timestamp);
                const now = new Date();
                const hoursDiff = (now - savedTimestamp) / (1000 * 60 * 60); // Convert to hours
                
                // If less than 20 hours have passed, use saved date
                if (hoursDiff < 20 && !isNaN(savedDate.getTime())) {
                    return savedDate;
                }
            } catch (error) {
                // If parsing fails, fall back to current date
                console.error('Error parsing saved calendar date:', error);
            }
        }
        // Default to current date if no saved date or expired
        return new Date();
    });


    // Use custom hooks for data fetching
    const {
        baseEvents,
        userProfiles,
        calendarUsers,
        isLoading: eventsLoading,
        leavesLoading,
        userProfileLoading,
        selectedGoogleEventsLoading,
        selectedMicrosoftEventsLoading,
        leavesError,
        eventsError,
        selectedGoogleEventsError,
        selectedMicrosoftEventsError,
        refetchLeaves,
        refetchEvents,
        refetchHolidays,
        refetchSelectedGoogleEvents,
        refetchSelectedMicrosoftEvents,
        refetchSyncedImportEvents,
        syncedEventIds,
        syncedEventIdSet,
        syncedImportEventsLoading,
    } = useCalendarEvents({
        token,
        selectedUsers: filterState.selectedUsers,
        user,
        tz,
        dummyImage,
        setIsGoogleTokenInvalidModalOpen,
        setIsMicrosoftTokenInvalidModalOpen,
        currentDate,
        view: uiState.view,
        leaveFilterUsers: filterState.leaveFilterUsers,
        useSelectedUsersForLeaves: user && (user.userType === SUPER_ADMIN || user.userType === ADMIN),
    });

    // Use custom hook for calendar sync
    const {
        isGoogleSyncVisible,
        isMicrosoftSyncVisible,
        googleUsers,
        microsoftUsers,
        isMicrosoftTokenChecking,
        isGoogleTokenChecking,
        setIsMicrosoftTokenChecking,
    } = useCalendarSync({ token, user });

    // Note: Google and Microsoft events now automatically refetch when selectedUsers changes
    // This is handled by React Query's query key dependency system in useCalendarEvents
    // No manual refetching needed - React Query handles it automatically

    const location = useLocation();
    const hasRefetchedOnMountRef = useRef(false);

    // Refetch synced import events on mount to ensure isEventSynced is properly loaded
    // This is especially important when navigating from Timer, as React Query might use stale cached data
    useEffect(() => {
        if (token && refetchSyncedImportEvents && !hasRefetchedOnMountRef.current) {
            // Refetch synced events when component mounts
            refetchSyncedImportEvents();
            hasRefetchedOnMountRef.current = true;
        }
    }, [token, refetchSyncedImportEvents]); // OLD CODE - Commented out: location.pathname dependency removed to avoid unnecessary refetches

    useEffect(() => {
        // Store current date with timestamp (expires after 20 hours)
        const dataToStore = {
            date: currentDate.toISOString(),
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('calendar.currentDate', JSON.stringify(dataToStore));
    }, [currentDate]);

    // Force calendar to highlight today's date on initial load only if no valid saved date
    useEffect(() => {
        const savedData = localStorage.getItem('calendar.currentDate');
        if (!savedData) {
            // No saved date, use today
            const today = new Date();
            setCurrentDate(today);
        } else {
            try {
                const { date, timestamp } = JSON.parse(savedData);
                const savedDate = new Date(date);
                const savedTimestamp = new Date(timestamp);
                const now = new Date();
                const hoursDiff = (now - savedTimestamp) / (1000 * 60 * 60); // Convert to hours
                
                // If more than 20 hours have passed, reset to current month
                if (hoursDiff >= 20 || isNaN(savedDate.getTime())) {
            const today = new Date();
            setCurrentDate(today);
                }
            } catch (error) {
                // If parsing fails, use today
                console.error('Error parsing saved calendar date:', error);
                const today = new Date();
                setCurrentDate(today);
            }
        }
    }, []); // Run only once on mount

    const handleNavigate = useCallback((newDate, newView) => {
        setCurrentDate(newDate);
        if (newView && newView !== uiState.view) {
            dispatchUI({ type: uiStateActions.SET_VIEW, view: newView });
        }
    }, [uiState.view]);

    // OLD CODE - Commented out isSelectionMode check
    const handleViewChange = useCallback((newView) => {
        // Prevent switching to month view when in selection mode
        // if (isSelectionMode && newView === 'month') {
        //     return;
        // }
        dispatchUI({ type: uiStateActions.SET_VIEW, view: newView });
    }, []);

    // Calendar sync is now handled by useCalendarSync hook

    // Fetch user suggestions for both admin and non-admin users
    const { data: userSuggestions = [], isLoading: isLeaveUserSuggestionsLoading } = useUserSuggestions(token);

    // Helper function to get user display name
    const getUserDisplayName = useCallback((u) => {
        if (u.first_name) {
            return `${u.first_name} ${u.middle_name && u.middle_name !== "null" ? u.middle_name + " " : ""}${u.last_name || ""}`.trim();
        } else if (u.company_first_name) {
            return `${u.company_first_name} ${u.company_last_name || ""}`.trim();
        }
        return u.company_name || '';
    }, []);

    // For admins/superadmins: Merge calendarUsers with userSuggestions, excluding duplicates
    // Compare calendarUsers.userId with userSuggestions._id
    const mergedUsersForAdmin = useMemo(() => {
        if (!(user && (user.userType === SUPER_ADMIN || user.userType === ADMIN))) {
            return calendarUsers;
        }
        
        // Get calendarUsers userIds as strings for comparison
        const calendarUserIds = new Set(calendarUsers.map(u => u.userId?.toString()).filter(Boolean));
        
        // Add calendarUsers first
        const merged = [...calendarUsers];
        
        // Add userSuggestions that are not already in calendarUsers
        userSuggestions.forEach(suggestion => {
            const suggestionId = suggestion._id?.toString();
            if (suggestionId && !calendarUserIds.has(suggestionId)) {
                // Convert userSuggestion format to match calendarUsers format
                merged.push({
                    userId: suggestion._id,
                    username: getUserDisplayName(suggestion),
                    email: suggestion.email,
                    // Include other fields if needed
                });
            }
        });
        
        return merged;
    }, [calendarUsers, userSuggestions, user, getUserDisplayName]);

    // Use the same user list as the dropdown (mergedUsersForAdmin for admin, calendarUsers otherwise)
    // so "All Users" checkbox stays in sync when user selects/deselects all
    useEffect(() => {
        const userList = (user && (user.userType === SUPER_ADMIN || user.userType === ADMIN))
            ? mergedUsersForAdmin
            : calendarUsers;
        if (userList.length === 0) return;
        const allUserIds = userList
            .map((u) => u.userId)
            .filter(Boolean)
            .map((id) => id?.toString())
            .sort()
            .join(',');
        const selectedUserIds = selectedUsers
            .map((id) => id?.toString())
            .filter(Boolean)
            .sort()
            .join(',');
        setSelectAllUsers(allUserIds === selectedUserIds);
    }, [selectedUsers, calendarUsers, mergedUsersForAdmin, user]);

    // Calculate selectAllLeaveUsers based on leaveFilterUsers
    useEffect(() => {
        if (userSuggestions.length === 0) return;
        const allUserIds = userSuggestions.map((user) => user._id?.toString()).filter(Boolean).sort().toString();
        const selectedUserIds = (leaveFilterUsers || []).map(id => id?.toString()).filter(Boolean).sort().toString();
        const isAllSelected = allUserIds === selectedUserIds;
        setSelectAllLeaveUsers(isAllSelected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaveFilterUsers, userSuggestions]);

    // Helper functions for leave user dropdown
    const openLeaveUserDropdown = useCallback(() => setIsLeaveUserDropdownOpen(true), [setIsLeaveUserDropdownOpen]);
    const closeLeaveUserDropdown = useCallback(() => setIsLeaveUserDropdownOpen(false), [setIsLeaveUserDropdownOpen]);

    const timeList = Array.from({ length: 24 }, (_, i) => {
        const hour12 = new Date(0, 0, 0, i).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });

        return {
            time: hour12,
            time_value: i, // Numeric value from 0 to 23
        };
    });

    const toggleDropdown = useCallback(() => {
        dispatchUI({ type: uiStateActions.SET_DROPDOWN, dropdownName: 'isOpen', isOpen: !uiState.isOpen });
    }, [uiState.isOpen]);

    // buildTitles is now in EventDataProcessor.js

    const [optimisticEdits, setOptimisticEdits] = useState({}); // key -> { start, end, allDay }

    const LOCAL_ONLY_TYPES = new Set(['Holiday', 'Leave', 'Birthday', 'Anniversary']);

    // baseEvents is now provided by useCalendarEvents hook
    // All event processing is handled by EventDataProcessor.js
    // Event transformation utilities are in eventTransformUtils.js

    const events = useMemo(
        () => applyOptimistic(baseEvents, optimisticEdits),
        [baseEvents, optimisticEdits]
    );

    // Synced event lookup helpers
    const syncedEventIdLookup = useMemo(() => syncedEventIdSet || new Set(), [syncedEventIdSet]);

    const getImportEventId = useCallback((event) => {
        return event?.extendedProps?.googleEventId
            || event?.extendedProps?.microsoftEventId
            || event?.extendedProps?.localEventId
            || event?.googleEventId
            || event?.microsoftEventId
            || event?.localEventId
            || event?._id
            || event?.id
            || null;
    }, []);

    const isEventSynced = useCallback((event) => {
        const id = getImportEventId(event);
        if (!id) return false;
        return syncedEventIdLookup.has(String(id));
    }, [getImportEventId, syncedEventIdLookup]);

    const filteredEvents = useMemo(
        () => filterEvents(events, uiState.filter, filterState.selectedCategories),
        [events, uiState.filter, filterState.selectedCategories]
    );

    const groupedEvents = useMemo(() => groupEventsByKey(filteredEvents), [filteredEvents]);

    // Event component for rendering (moved to CalendarView, keeping here for compatibility)
    // Memoized to prevent unnecessary re-renders when props haven't changed
    // OLD CODE - Commented out isSelectionMode prop
    // const Event = React.memo(({ event, view, isSelectionMode, selectedEventsForImport = [], setSelectedEventsForImport, eventKey: getEventKey }) => {
    const Event = React.memo(({ event, view, selectedEventsForImport = [], setSelectedEventsForImport, eventKey: getEventKey }) => {
        const displayTitle = event.title || 'Untitled Event';
        const displayTime = event.displayStartTime || event.startTime;
        const eventType = event.eventType || event.extendedProps?.eventType;
        // Only selectable if it's an Event type AND has start/end times (not all-day)
        const isAllDay = event.allDay === true || !event.start || !event.end;
        const hasStartEndTime = event.start && event.end && !isAllDay;
        const isSelectableType = ['Event', 'GoogleEvent', 'MicrosoftEvent'].includes(eventType) && hasStartEndTime;
        const isSyncedToTracker = isEventSynced(event);
        // Check if event end time is before current time
        const isEventEnded = event.end ? moment(event.end).isBefore(moment()) : false;
        const isSelectable = isSelectableType && !isSyncedToTracker && isEventEnded;
        // OLD CODE - Commented out isSelectionMode check
        // const canRenderCheckbox = isSelectionMode && isSelectableType && !isSyncedToTracker && isEventEnded;
        const canRenderCheckbox = false; // Always false since isSelectionMode is not used
        const isSelected = Array.isArray(selectedEventsForImport) && selectedEventsForImport.some(ev => {
            if (!ev || !getEventKey) return false;
            const evKey = getEventKey(ev);
            const currentKey = getEventKey(event);
            return evKey === currentKey;
        });

        const [tooltipPosition, setTooltipPosition] = React.useState({ top: 0, left: 0, visible: false });
        const syncedSectionRef = React.useRef(null);

        const updateTooltipPosition = React.useCallback(() => {
            if (syncedSectionRef.current && tooltipPosition.visible) {
                const rect = syncedSectionRef.current.getBoundingClientRect();
                const tooltipLeft = rect.right + 8; // 8px spacing to the right
                const tooltipTop = rect.top + (rect.height / 2); // Center vertically
                setTooltipPosition(prev => ({
                    top: tooltipTop,
                    left: tooltipLeft,
                    visible: prev.visible
                }));
            }
        }, [tooltipPosition.visible]);

        React.useEffect(() => {
            if (tooltipPosition.visible) {
                window.addEventListener('scroll', updateTooltipPosition, true);
                window.addEventListener('resize', updateTooltipPosition);
                return () => {
                    window.removeEventListener('scroll', updateTooltipPosition, true);
                    window.removeEventListener('resize', updateTooltipPosition);
                };
            }
        }, [tooltipPosition.visible, updateTooltipPosition]);

        const handleCheckboxChange = (e) => {
            e.stopPropagation();
            if (!setSelectedEventsForImport || !getEventKey || !isSelectable) return;
            if (isSelected) {
                setSelectedEventsForImport(selectedEventsForImport.filter(ev => {
                    const evKey = getEventKey(ev);
                    const currentKey = getEventKey(event);
                    return evKey !== currentKey;
                }));
            } else {
                setSelectedEventsForImport([...selectedEventsForImport, event]);
            }
        };

        const handleSyncedIconMouseEnter = (e) => {
            if (syncedSectionRef.current) {
                const rect = syncedSectionRef.current.getBoundingClientRect();
                // Position tooltip above the icon, centered horizontally
                const tooltipLeft = rect.left + (rect.width / 2); // Center horizontally
                const tooltipTop = rect.top - 8; // 8px above the icon
                setTooltipPosition({
                    top: tooltipTop,
                    left: tooltipLeft,
                    visible: true
                });
            }
        };

        const handleSyncedIconMouseLeave = () => {
            setTooltipPosition(prev => ({ ...prev, visible: false }));
        };

        // In month view, show title with synced icon if synced
        if (view === 'month') {
            if (isSyncedToTracker) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div
                            className="synced-section"
                            ref={syncedSectionRef}
                            onMouseEnter={handleSyncedIconMouseEnter}
                            onMouseLeave={handleSyncedIconMouseLeave}
                            style={{ display: 'inline-flex', flexShrink: 0 }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                <path d="M11.6631 3.79159C10.7173 1.58008 8.55186 0.150391 6.14646 0.150391C4.83879 0.150391 3.5831 0.574297 2.54736 1.35013V0.550303C2.54736 0.330351 2.3674 0.150391 2.14746 0.150391C1.92751 0.150391 1.74756 0.330351 1.74756 0.550303V2.14995C1.74756 2.33391 1.87153 2.49387 2.04948 2.53786L3.64909 2.93778C3.68108 2.94578 3.71507 2.94977 3.74706 2.94977C3.92702 2.94977 4.08898 2.8278 4.13496 2.64784C4.18895 2.43389 4.05898 2.21594 3.84304 2.16195L3.05923 1.96599C3.95101 1.30814 5.02674 0.950215 6.14446 0.950215C8.22994 0.950215 10.1055 2.18994 10.9253 4.10752C11.2032 4.75338 11.3432 5.43923 11.3432 6.14907C11.3432 6.85891 11.2032 7.54476 10.9253 8.19062C10.8373 8.39458 10.9313 8.62852 11.1352 8.71651C11.1872 8.7385 11.2412 8.7485 11.2932 8.7485C11.4491 8.7485 11.5951 8.65852 11.6611 8.50655C11.981 7.76072 12.143 6.96689 12.143 6.14907C12.143 5.33125 11.981 4.53742 11.6611 3.79159H11.6631Z" fill="#0369A1" stroke="#0369A1" strokeWidth="0.3" />
                                <path d="M10.2434 9.76055L8.64379 9.36064C8.42984 9.30665 8.21189 9.43663 8.15791 9.65258C8.10392 9.86653 8.23389 10.0845 8.44984 10.1385L9.23364 10.3344C8.34186 10.9923 7.26613 11.3502 6.14841 11.3502C4.06293 11.3502 2.18739 10.1105 1.3676 8.1929C1.08967 7.54704 0.949703 6.86119 0.949703 6.15135C0.949703 5.4415 1.08967 4.75565 1.3676 4.1098C1.45558 3.90584 1.3616 3.67189 1.15765 3.58391C0.953702 3.49793 0.71976 3.58991 0.631782 3.79387C0.311862 4.5397 0.149902 5.33353 0.149902 6.15135C0.149902 6.96917 0.311862 7.76299 0.631782 8.50883C1.57755 10.7203 3.74301 12.15 6.14841 12.15C7.45608 12.15 8.71177 11.7261 9.74751 10.9503V11.7501C9.74751 11.9701 9.92747 12.15 10.1474 12.15C10.3674 12.15 10.5473 11.9701 10.5473 11.7501V10.1505C10.5473 9.96651 10.4233 9.80654 10.2454 9.76255L10.2434 9.76055Z" fill="#0369A1" stroke="#0369A1" strokeWidth="0.3" />
                                <path d="M5.34674 8.14832C5.44871 8.14832 5.55069 8.10833 5.62867 8.03034L9.02782 4.63109C9.18378 4.47513 9.18378 4.22118 9.02782 4.06522C8.87186 3.90925 8.61793 3.90925 8.46196 4.06522L5.34474 7.18253L4.02707 5.86482C3.87111 5.70885 3.61717 5.70885 3.46121 5.86482C3.30525 6.02079 3.30525 6.27473 3.46121 6.4307L5.06081 8.03034C5.13879 8.10833 5.24077 8.14832 5.34274 8.14832H5.34674Z" fill="#0369A1" stroke="#0369A1" strokeWidth="0.3" />
                            </svg>
                        </div>
                        {tooltipPosition.visible && (
                            <div
                                className="synced-tooltip"
                                style={{
                                    top: `${tooltipPosition.top}px`,
                                    left: `${tooltipPosition.left}px`,
                                }}
                            >
                                Synced to time-tracker
                            </div>
                        )}
                        <SafeHtml html={displayTitle} />
                    </div>
                );
            }
            return <SafeHtml html={displayTitle} />;
        }

        // In other views, show time + title if time exists
        return (
            <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: isSyncedToTracker ? 1 : 1 }}
                // OLD CODE - Commented out isSelectionMode check
                // className={((isSyncedToTracker && isSelectionMode && isSelectableType) || canRenderCheckbox) ? 'event-time-active' : ''}
                className={isSyncedToTracker ? 'event-time-active' : ''}
            >
                <div className="sync-input">
                    {/* OLD CODE - Commented out checkbox rendering */}
                    {/* {canRenderCheckbox && (
                        <input
                            className="input-right"
                            type="checkbox"
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: 'pointer', flexShrink: 0 }}
                        />
                    )} */}
                    {(isSyncedToTracker && isSelectableType) && (
                        <>
                            <div
                                className="synced-section"
                                ref={syncedSectionRef}
                                onMouseEnter={handleSyncedIconMouseEnter}
                                onMouseLeave={handleSyncedIconMouseLeave}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <path d="M11.6631 3.79159C10.7173 1.58008 8.55186 0.150391 6.14646 0.150391C4.83879 0.150391 3.5831 0.574297 2.54736 1.35013V0.550303C2.54736 0.330351 2.3674 0.150391 2.14746 0.150391C1.92751 0.150391 1.74756 0.330351 1.74756 0.550303V2.14995C1.74756 2.33391 1.87153 2.49387 2.04948 2.53786L3.64909 2.93778C3.68108 2.94578 3.71507 2.94977 3.74706 2.94977C3.92702 2.94977 4.08898 2.8278 4.13496 2.64784C4.18895 2.43389 4.05898 2.21594 3.84304 2.16195L3.05923 1.96599C3.95101 1.30814 5.02674 0.950215 6.14446 0.950215C8.22994 0.950215 10.1055 2.18994 10.9253 4.10752C11.2032 4.75338 11.3432 5.43923 11.3432 6.14907C11.3432 6.85891 11.2032 7.54476 10.9253 8.19062C10.8373 8.39458 10.9313 8.62852 11.1352 8.71651C11.1872 8.7385 11.2412 8.7485 11.2932 8.7485C11.4491 8.7485 11.5951 8.65852 11.6611 8.50655C11.981 7.76072 12.143 6.96689 12.143 6.14907C12.143 5.33125 11.981 4.53742 11.6611 3.79159H11.6631Z" fill="#0369A1" stroke="#0369A1" strokeWidth="0.3" />
                                    <path d="M10.2434 9.76055L8.64379 9.36064C8.42984 9.30665 8.21189 9.43663 8.15791 9.65258C8.10392 9.86653 8.23389 10.0845 8.44984 10.1385L9.23364 10.3344C8.34186 10.9923 7.26613 11.3502 6.14841 11.3502C4.06293 11.3502 2.18739 10.1105 1.3676 8.1929C1.08967 7.54704 0.949703 6.86119 0.949703 6.15135C0.949703 5.4415 1.08967 4.75565 1.3676 4.1098C1.45558 3.90584 1.3616 3.67189 1.15765 3.58391C0.953702 3.49793 0.71976 3.58991 0.631782 3.79387C0.311862 4.5397 0.149902 5.33353 0.149902 6.15135C0.149902 6.96917 0.311862 7.76299 0.631782 8.50883C1.57755 10.7203 3.74301 12.15 6.14841 12.15C7.45608 12.15 8.71177 11.7261 9.74751 10.9503V11.7501C9.74751 11.9701 9.92747 12.15 10.1474 12.15C10.3674 12.15 10.5473 11.9701 10.5473 11.7501V10.1505C10.5473 9.96651 10.4233 9.80654 10.2454 9.76255L10.2434 9.76055Z" fill="#0369A1" stroke="#0369A1" strokeWidth="0.3" />
                                    <path d="M5.34674 8.14832C5.44871 8.14832 5.55069 8.10833 5.62867 8.03034L9.02782 4.63109C9.18378 4.47513 9.18378 4.22118 9.02782 4.06522C8.87186 3.90925 8.61793 3.90925 8.46196 4.06522L5.34474 7.18253L4.02707 5.86482C3.87111 5.70885 3.61717 5.70885 3.46121 5.86482C3.30525 6.02079 3.30525 6.27473 3.46121 6.4307L5.06081 8.03034C5.13879 8.10833 5.24077 8.14832 5.34274 8.14832H5.34674Z" fill="#0369A1" stroke="#0369A1" strokeWidth="0.3" />
                                </svg>
                            </div>
                            {tooltipPosition.visible && (
                                <div
                                    className="synced-tooltip"
                                    style={{
                                        top: `${tooltipPosition.top}px`,
                                        left: `${tooltipPosition.left}px`,
                                    }}
                                >
                                    Synced to time-tracker
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div style={{ flex: 1 }} >
                    {displayTime && (
                        <span className="event-time">{displayTime} </span>
                    )}
                    <SafeHtml html={displayTitle} />
                </div>
            </div>
        );
    });

    const debouncedFilterChange = useMemo(
        () => debounce((value) => {
            dispatchUI({ type: uiStateActions.SET_FILTER, filter: value });
        }, 500),
        [dispatchUI]
    );
    const handleFilterChange = (e) => {
        const searchTerm = e.target.value;
        debouncedFilterChange(searchTerm);
    };

    const openUserDropdown = useCallback(() => {
        setIsUserLoading(true);
        setIsUserDropdownOpen(true);
        setTimeout(() => setIsUserLoading(false), 0);
    }, []);
    const closeUserDropdown = useCallback(() => setIsUserDropdownOpen(false), []);

    const getEventTypeLabel = () => {
        if (selectedEvents.length === 0) return "Events";
        const eventType = selectedEvents[0].eventType || "Event";
        // Map eventType to plural form for the title
        const typeMap = {
            Leave: "Time Off Request",
            Event: "Event",
            Holiday: "Holiday",
            Birthday: "Birthday",
            Anniversary: "Anniversary",
        };
        return typeMap[eventType] || "Events";
    };

    const handleSelectSlot = ({ start, end }) => {
        // Prevent opening modal if it's already open, if submit is loading, or if modal is closing
        if (createEventModalOpen || isSubmitLoading || isClosingModalRef.current) {
            return;
        }

        const startDate = toAppTimezone(start, tz);
        const endDate = toAppTimezone(end, tz);
        // const isSameDay = startDate.isSame(endDate, "day");
        const rawEndDate = toAppTimezone(end, tz);
        const adjustedEndDateForComparison = rawEndDate.clone().subtract(1, 'second');
        const isSameDay = startDate.isSame(adjustedEndDateForComparison, "day");
        setEventDate(startDate.toDate());

        if (isSameDay) {
            const startHour = startDate.hour();
            const endHour = endDate.hour();

            const startTimeMatch = timeList.reduce((prev, curr) => {
                const prevDiff = Math.abs(prev.time_value - startHour);
                const currDiff = Math.abs(curr.time_value - startHour);
                return currDiff < prevDiff ? curr : prev;
            }, timeList[0]);

            const endTimeMatch = timeList.reduce((prev, curr) => {
                const prevDiff = Math.abs(prev.time_value - endHour);
                const currDiff = Math.abs(curr.time_value - endHour);
                return currDiff < prevDiff ? curr : prev;
            }, timeList[0]);

            setStartTime(startTimeMatch.time_value);
            setEndTime(endTimeMatch.time_value);
            setEventStartTime(startDate);
            setEventEndTime(endDate);
        } else {
            setStartTime("");
            setEndTime("");
            setEventStartTime("");
            setEventEndTime("");
        }
        setEventName("");
        setEventDescription("");
        setEventLocation("");
        setEventOrganizer(user?._id);
        setEventAttendees([]);
        setEventBanner(null);
        setIsVirtual(false);
        setEventMeetingLink("");
        setRepeatType(isSameDay ? "does_not_repeat" : "all_day");
        setEventNameIsValid(false);
        setEventDateIsValid(false);
        setIsSlotSelected(true);
        setCreateEventModalOpen(true);
    };

    // Wrapper for setCreateEventModalOpen that tracks closing state
    const handleSetCreateEventModalOpen = useCallback((value) => {
        if (value === false && !isSubmitLoading) {
            // When closing, set the ref to prevent reopening during animation
            isClosingModalRef.current = true;
            setCreateEventModalOpen(false);
            // Clear the closing flag after the close animation completes (200ms)
            setTimeout(() => {
                isClosingModalRef.current = false;
            }, 250); // Slightly longer than closeTimeoutMS to ensure it's cleared after animation
        } else {
            // When opening, just set the state normally
            setCreateEventModalOpen(value);
        }
    }, [isSubmitLoading]);

    const handleOpenCreateEventModal = useCallback(() => {
        if (isSubmitLoading) return;
        if (!isSubmitLoading) {
            setCreateEventModalOpen(true);
            setEventDate(null);
            setStartTime("");
            setEndTime("");
            setEventStartTime("");
            setEventEndTime("");
            setStartTimeString("");
            setEndTimeString("");
            setStartTimeIsValid(false);
            setEndTimeIsValid(false);
            setEventName("");
            setEventDescription("");
            setEventLocation("");
            setEventOrganizer(user?._id);
            setEventAttendees([]);
            setEventBanner(null);
            setIsVirtual(false);
            setEventMeetingLink("");
            setRepeatType("does_not_repeat");
            setEventNameIsValid(false);
            setEventDateIsValid(false);
            setEventStartTimeIsValid(false);
            setEventEndTimeIsValid(false);
            setRepeatTypeIsValid(false);
            setOrganizerDropdownOpen(false);
            setRepeatTypeDropdownOpen(false);
        }
    }, [isSubmitLoading, user?._id]);

    const handleOpenCreateHolidayModal = useCallback(() => {
        setCreateHolidayModalOpen(true);
        setIsSlotSelected(false);
    }, []);

    const handleImportSelectedEvents = useCallback(async () => {
        // Filter for events that are unsynced AND have ended (end time before current time)
        const selectableSelectedEvents = (selectedEventsForImport || []).filter(ev => {
            if (isEventSynced(ev)) return false;
            if (!ev.end) return false;
            return moment(ev.end).isBefore(moment());
        });

        if (!selectableSelectedEvents || selectableSelectedEvents.length === 0) {
            toast.error('Please select at least one event that has ended', { position: "bottom-right", autoClose: 1500 });
            return;
        }

        // Cleanup any non-selectable events that might have been in selection due to stale state
        if (selectableSelectedEvents.length !== (selectedEventsForImport?.length || 0)) {
            setSelectedEventsForImport(selectableSelectedEvents);
        }

        // Check if import is already in progress
        if (isImportLoading) {
            return;
        }

        try {
            setIsImportLoading(true);
            importCanceledRef.current = false;

            // Prepare events data for import
            const eventsToImport = selectableSelectedEvents.map(event => {
                const eventType = event.eventType || event.extendedProps?.eventType;
                const title = event.titlePlain || event.title || 'Untitled Event';
                // Sanitize title - remove HTML tags
                const sanitizedTitle = title.replace(/<[^>]*>/g, '').trim();

                const start = moment(event.start);
                const end = moment(event.end || event.start);

                return {
                    title: sanitizedTitle,
                    startTime: start.toISOString(),
                    endTime: end.toISOString(),
                    date: start.format('YYYY-MM-DD'),
                    eventType: eventType,
                    googleEventId: event.extendedProps?.googleEventId,
                    microsoftEventId: event.extendedProps?.microsoftEventId,
                    localEventId: event.extendedProps?.localEventId || event._id,
                };
            });

            // Save to import database
            const response = await axios.post('/api/time-track/importdata/calendar-events', {
                events: eventsToImport,
                tz: global.config.getTimeZone() || 'UTC',
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data.success && !importCanceledRef.current) {
                // Wait a moment for the backend to process
                await new Promise(resolve => setTimeout(resolve, 500));

                // Check again if canceled during wait
                if (!importCanceledRef.current) {
                    // Navigate to TrackerImportData page
                    navigate('/user/time-tracker/timer', {
                        state: {
                            fromCalendar: true,
                            importData: eventsToImport,
                            dateRange: null, // Not using date range for selected events
                        }
                    });

                    // Reset selection mode
                    setIsSelectionMode(false);
                    setSelectedEventsForImport([]);
                    setSelectAllEvents(false);
                }
            }
        } catch (error) {
            if (!importCanceledRef.current) {
                console.error('Error importing events:', error);
                toast.error('Failed to import events. Please try again.', { position: "bottom-right", autoClose: 1500 });
            }
        } finally {
            setIsImportLoading(false);
            importCanceledRef.current = false;
        }
    }, [selectedEventsForImport, token, navigate, isImportLoading, setIsImportLoading, isEventSynced]);

    // Get current view date range (week/day/month)
    const getCurrentViewDateRange = useCallback(() => {
        // if (view === 'month') return { startDate: null, endDate: null };
        const baseDate = moment.tz(currentDate, tz);
        let viewStart, viewEnd;

        if (view === 'month') {
            // OLD CODE - Commented out: if (view === 'month') return { startDate: null, endDate: null };
            viewStart = baseDate.clone().startOf('month').startOf('day');
            viewEnd = baseDate.clone().endOf('month').endOf('day');
        } else if (view === 'week') {
            const weekStart = appSettings?.weekStartDay === 'Monday'
                ? baseDate.clone().startOf('isoWeek')
                : baseDate.clone().startOf('week');
            viewStart = weekStart.startOf('day');
            viewEnd = weekStart.clone().add(6, 'days').endOf('day');
        } else if (view === 'day') {
            viewStart = baseDate.clone().startOf('day');
            viewEnd = baseDate.clone().endOf('day');
        } else {
            // OLD CODE - Commented out: return { startDate: null, endDate: null };
            return { startDate: null, endDate: null };
        }

        return {
            startDate: viewStart.toDate(),
            endDate: viewEnd.toDate(),
        };
    }, [view, currentDate, tz, appSettings?.weekStartDay]);

    // Get events in current view (week/day only)
    const getEventsInCurrentView = useCallback(() => {
        if (view === 'month') return [];

        const baseDate = moment.tz(currentDate, tz);
        let viewStart, viewEnd;

        if (view === 'week') {
            const weekStart = appSettings?.weekStartDay === 'Monday'
                ? baseDate.clone().startOf('isoWeek')
                : baseDate.clone().startOf('week');
            viewStart = weekStart.startOf('day');
            viewEnd = weekStart.clone().add(6, 'days').endOf('day');
        } else if (view === 'day') {
            viewStart = baseDate.clone().startOf('day');
            viewEnd = baseDate.clone().endOf('day');
        } else {
            return [];
        }

        return filteredEvents.filter(event => {
            const eventType = event.eventType || event.extendedProps?.eventType;
            if (!['Event', 'GoogleEvent', 'MicrosoftEvent'].includes(eventType)) {
                return false;
            }
            // Only include events with start and end times (not all-day)
            const isAllDay = event.allDay === true || !event.start || !event.end;
            if (isAllDay) return false;

            const eventStart = moment(event.start);
            return eventStart.isSameOrAfter(viewStart) && eventStart.isSameOrBefore(viewEnd);
        });
    }, [view, currentDate, tz, filteredEvents, appSettings?.weekStartDay]);

    // OLD CODE - Commented out isSelectionMode check
    // Close user dropdown when selection mode is activated
    // useEffect(() => {
    //     if (isSelectionMode && isUserDropdownOpen) {
    //         closeUserDropdown();
    //     }
    // }, [isSelectionMode, isUserDropdownOpen, closeUserDropdown]);

    // Auto-update selectAllEvents when all events in view are selected
    useEffect(() => {
        if (!isSelectionMode || view === 'month') return;

        const eventsInView = getEventsInCurrentView();
        // Filter for selectable events: unsynced AND ended (end time before current time)
        const selectableEventsInView = eventsInView.filter(ev => {
            if (isEventSynced(ev)) return false;
            if (!ev.end) return false;
            return moment(ev.end).isBefore(moment());
        });

        if (selectableEventsInView.length === 0) {
            if (selectAllEvents) {
                setSelectAllEvents(false);
            }
            return;
        }

        const allSelected = selectableEventsInView.every(ev => {
            const evKey = eventKey(ev);
            return selectedEventsForImport.some(selected => {
                const selectedKey = eventKey(selected);
                return evKey === selectedKey;
            });
        });

        if (allSelected !== selectAllEvents) {
            setSelectAllEvents(allSelected);
        }
    }, [selectedEventsForImport, isSelectionMode, view, getEventsInCurrentView, selectAllEvents, eventKey, isEventSynced]);

    const handleConfirmImportWithDateRange = useCallback(async (dateRangeParam) => {
        // Use the parameter if provided, otherwise fall back to state
        const rangeToUse = dateRangeParam || importDateRange;

        if (!rangeToUse || !rangeToUse.startDate || !rangeToUse.endDate) {
            toast.error('Please select a date range', { position: "bottom-right", autoClose: 1500 });
            return;
        }

        // Check if import is already in progress
        if (isImportLoading) {
            return;
        }

        try {
            setIsImportLoading(true);
            importCanceledRef.current = false;
            // Don't close modal - keep it open during import
            // setIsImportDateRangeModalOpen(false); // Close modal while loading

            const startDate = moment(rangeToUse.startDate).startOf('day');
            const endDate = moment(rangeToUse.endDate).endOf('day');

            // Get all events in the date range that are unsynced AND have ended
            const eventsInRange = filteredEvents.filter(event => {
                const eventType = event.eventType || event.extendedProps?.eventType;
                if (!['Event', 'GoogleEvent', 'MicrosoftEvent'].includes(eventType)) {
                    return false;
                }
                // Only include events with start and end times (not all-day)
                const isAllDay = event.allDay === true || !event.start || !event.end;
                if (isAllDay) return false;

                // Check if synced
                if (isEventSynced(event)) return false;

                // Check if event end time is before current time
                if (!event.end) return false;
                if (!moment(event.end).isBefore(moment())) return false;

                const eventStart = moment(event.start);
                return eventStart.isSameOrAfter(startDate) && eventStart.isSameOrBefore(endDate);
            });

            if (eventsInRange.length === 0) {
                // Toast z-index is now set in custom.css (100000) to appear above modal (z-index: 99999)
                toast.error('No selectable events found in the selected date range. Only events that have ended can be imported.', { 
                    position: "bottom-right", 
                    autoClose: 1500
                });
                setIsImportLoading(false);
                // Keep modal open - don't close it
                // setIsImportDateRangeModalOpen(true, rangeToUse); // Reopen modal
                return;
            }

            // Prepare events data for import
            const eventsToImport = eventsInRange.map(event => {
                const eventType = event.eventType || event.extendedProps?.eventType;
                const title = event.titlePlain || event.title || 'Untitled Event';
                const sanitizedTitle = title.replace(/<[^>]*>/g, '').trim();

                const start = moment(event.start);
                const end = moment(event.end || event.start);

                return {
                    title: sanitizedTitle,
                    startTime: start.toISOString(),
                    endTime: end.toISOString(),
                    date: start.format('YYYY-MM-DD'),
                    eventType: eventType,
                    googleEventId: event.extendedProps?.googleEventId,
                    microsoftEventId: event.extendedProps?.microsoftEventId,
                    localEventId: event.extendedProps?.localEventId || event._id,
                };
            });

            // Save to import database
            const response = await axios.post('/api/time-track/importdata/calendar-events', {
                events: eventsToImport,
                tz: global.config.getTimeZone() || 'UTC',
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data.success && !importCanceledRef.current) {
                // Wait a moment for the backend to process
                await new Promise(resolve => setTimeout(resolve, 500));

                // Check again if canceled during wait
                if (!importCanceledRef.current) {
                    // Invalidate cache to ensure fresh data is fetched from database on mount
                    // OLD CODE - Commented out: Prefetching data caused delays and complexity
                    // Updated: Simply invalidate cache so component always fetches fresh data from DB
                    try {
                        queryClient.invalidateQueries('timerDataImportData');
                        queryClient.invalidateQueries('groupTimerDataImportData');
                        // Remove cached data to force fresh fetch
                        queryClient.removeQueries('timerDataImportData');
                        queryClient.removeQueries('groupTimerDataImportData');
                    } catch (error) {
                        console.warn('Failed to invalidate query cache:', error);
                    }

                    // Navigate to TrackerImportData page
                    navigate('/user/time-tracker/timer', {
                        state: {
                            fromCalendar: true,
                            importData: eventsToImport,
                            dateRange: {
                                startDate: rangeToUse.startDate,
                                endDate: rangeToUse.endDate,
                            },
                        }
                    });

                    // Reset selection mode
                    setIsSelectionMode(false);
                    setSelectedEventsForImport([]);
                    setSelectAllEvents(false);
                }
            }
        } catch (error) {
            if (!importCanceledRef.current) {
                console.error('Error importing events:', error);
                toast.error('Failed to import events. Please try again.', { position: "bottom-right", autoClose: 1500 });
                // Keep modal open on error - don't close it
                // setIsImportDateRangeModalOpen(true, rangeToUse); // Reopen modal on error
            }
        } finally {
            setIsImportLoading(false);
            importCanceledRef.current = false;
        }
    }, [importDateRange, filteredEvents, token, navigate, isImportLoading, setIsImportLoading, isEventSynced]);

    const CustomToolbar = React.memo(({ date, onNavigate, onView, view }) => {
        const formatLabel = () => {
            if (view === 'week') {
                const startOfWeek = moment(date).startOf('week');
                const endOfWeek = moment(date).endOf('week');
                return `${startOfWeek.format('MMM DD')} – ${endOfWeek.format('DD, YYYY')}`;
            }
            return moment(date).format(view === 'month' ? 'MMMM YYYY' : 'MMM DD, YYYY');
        };

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px',
                border: `1px solid ${CALENDAR_CONFIG.UI_COLORS.BORDER_LIGHT}`,
                borderRadius: '8px',
                backgroundColor: CALENDAR_CONFIG.UI_COLORS.BG_LIGHT,
                marginBottom: '10px',
            }}>
                <button
                    onClick={() => onNavigate('TODAY')}
                    style={{
                        padding: '8px 16px',
                        border: `1px solid ${CALENDAR_CONFIG.UI_COLORS.BORDER_LIGHT}`,
                        borderRadius: '20px',
                        backgroundColor: CALENDAR_CONFIG.UI_COLORS.BG_WHITE,
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
                    }}
                >
                    Today
                </button>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                        onClick={() => onNavigate('PREV')}
                        style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            marginRight: '10px',
                            color: CALENDAR_CONFIG.UI_COLORS.TEXT_SECONDARY,
                        }}
                    >
                        ←
                    </button>
                    <span style={{ fontSize: '16px', fontWeight: '500', color: CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY }}>
                        {formatLabel()}
                    </span>
                    <button
                        onClick={() => onNavigate('NEXT')}
                        style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            fontSize: '18px',
                            marginLeft: '10px',
                            color: CALENDAR_CONFIG.UI_COLORS.TEXT_SECONDARY,
                        }}
                    >
                        →
                    </button>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                        onClick={() => onView('week')}
                        className={`view-btn ${view === 'week' ? 'active' : ''} rounded-left`}
                        style={{
                            padding: '8px 16px',
                            border: `1px solid ${CALENDAR_CONFIG.UI_COLORS.BORDER_LIGHT}`,
                            borderRadius: view === 'week' ? '20px 0 0 20px' : '20px',
                            backgroundColor: view === 'week' ? CALENDAR_CONFIG.UI_COLORS.BG_ACTIVE : CALENDAR_CONFIG.UI_COLORS.BG_WHITE,
                            color: view === 'week' ? CALENDAR_CONFIG.UI_COLORS.TEXT_ACTIVE : CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                        }}
                    >
                        Week
                    </button>
                    <button
                        onClick={() => onView('month')}
                        className={`view-btn ${view === 'month' ? 'active' : ''}`}
                        style={{
                            padding: '8px 16px',
                            border: `1px solid ${CALENDAR_CONFIG.UI_COLORS.BORDER_LIGHT}`,
                            borderRadius: view === 'month' ? '0' : '20px',
                            backgroundColor: view === 'month' ? CALENDAR_CONFIG.UI_COLORS.BG_ACTIVE : CALENDAR_CONFIG.UI_COLORS.BG_WHITE,
                            color: view === 'month' ? CALENDAR_CONFIG.UI_COLORS.TEXT_ACTIVE : CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                        }}
                    >
                        Month
                    </button>
                    <button
                        onClick={() => onView('day')}
                        className={`view-btn ${view === 'day' ? 'active' : ''} rounded-right`}
                        style={{
                            padding: '8px 16px',
                            border: `1px solid ${CALENDAR_CONFIG.UI_COLORS.BORDER_LIGHT}`,
                            borderRadius: view === 'day' ? '0 20px 20px 0' : '20px',
                            backgroundColor: view === 'day' ? CALENDAR_CONFIG.UI_COLORS.BG_ACTIVE : CALENDAR_CONFIG.UI_COLORS.BG_WHITE,
                            color: view === 'day' ? CALENDAR_CONFIG.UI_COLORS.TEXT_ACTIVE : CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                        }}
                    >
                        Day
                    </button>
                </div>
            </div>
        );
    });

    const CustomDayHeader = ({ date }) => {
        const weekday = moment(date).format("ddd"); // Mon … Sun
        const dayNum = moment(date).format("D");   // 1 – 31

        return (
            <div className="day-header">
                <div className="weekday">{weekday}</div>
                <div className="day-num">{dayNum}</div>
            </div>
        );
    };

    const CustomMonthHeader = ({ date }) => {
        const label = moment(date).format("ddd").toUpperCase();
        return <span>{label}</span>;
    };

    const canDrag = (evt) => {
        const isGrouped = Array.isArray(evt?.extendedProps?.allEvents) && evt.extendedProps.allEvents.length > 1;
        const type = evt.extendedProps?.eventType;
        const allowedTypes = ['Event', 'GoogleEvent', 'MicrosoftEvent'];
        // return !isUpdating && !isGrouped && allowedTypes.includes(type) && !evt?.extendedProps?.readOnly;
        const isSynced = isEventSynced(evt);
        return !isUpdating && !isGrouped && allowedTypes.includes(type) && !evt?.extendedProps?.readOnly && !isSynced;
    };

    const canResize = (evt) => {
        const allDay = evt.allDay === true || !evt.start || !evt.end;
        return canDrag(evt) && !allDay;
    };

    // Normalize dates to your app TZ
    const toTZ = (date) => moment.tz(date, tz).toDate();

    const onEventDrop = async ({ event, start, end, isAllDay }) => {
        if (isUpdating) return;
        setIsUpdating(true);
        const newStart = toTZ(start);
        const newEnd = toTZ(end);
        const key = eventKey(event);

        const source = event?.extendedProps?.source || event?.source;
        const eventType = event?.extendedProps?.eventType || event?.eventType;

        // Handle unsupported event types immediately (no optimistic update needed)
        if (!['google', 'microsoft', 'local'].includes(source) && !LOCAL_ONLY_TYPES.has(eventType)) {
            toast.error("This event type cannot be updated via drag.", {
                position: "bottom-right",
                autoClose: 1500,
            });
            setIsUpdating(false);
            return;
        }

        // Handle local-only types (no API call needed)
        if (LOCAL_ONLY_TYPES.has(eventType)) {
            // Keep the optimistic move purely local - no rollback needed
            setOptimisticEdits((prev) => ({
                ...prev,
                [key]: { start: newStart, end: newEnd, allDay: isAllDay },
            }));
            setIsUpdating(false);
            return;
        }

        // Execute optimistic update with timeout and error recovery
        try {
            await executeOptimisticUpdate({
                setOptimisticEdits,
                key,
                optimisticData: { start: newStart, end: newEnd, allDay: isAllDay },
                operation: async () => {
                    // Execute the appropriate update function
                    if (source === 'google') {
                        await updateGoogleTimes(event, newStart, newEnd, isAllDay);
                    } else if (source === 'microsoft') {
                        await updateMicrosoftTimes(event, newStart, newEnd, isAllDay);
                    } else if (source === 'local') {
                        await updateLocalTimes(event, newStart, newEnd, isAllDay);
                    }

                    // Refresh data from server after successful update
                    await Promise.all([
                        refetchEvents?.(),
                        refetchSelectedGoogleEvents?.(),
                        refetchSelectedMicrosoftEvents?.(),
                    ]);
                },
                onSuccess: () => {
                    toast.success("Event time updated", { position: "bottom-right", autoClose: 1500 });
                },
                onError: (error, isTimeout) => {
                    const errorMessage = isTimeout
                        ? "The update took too long. Please try again."
                        : getErrorMessage(error);

                    console.error("Error updating event:", error);
                    toast.error(errorMessage, { position: "bottom-right", autoClose: 3000 });
                },
                timeoutMs: 30000, // 30 seconds timeout
                enableRetry: true, // Enable retry for retryable errors (network, 5xx, etc.)
            });
        } catch (error) {
            // This should not happen as errors are handled in onError callback
            // But keep as fallback for unexpected cases
            console.error("Unexpected error in optimistic update:", error);
            const errorMessage = getErrorMessage(error);
            toast.error(errorMessage, { position: "bottom-right", autoClose: 3000 });
        } finally {
            setIsUpdating(false);
        }
    };

    const onEventResize = ({ event, start, end }) =>
        onEventDrop({ event, start, end, isAllDay: event.allDay });


    const updateGoogleTimes = async (event, start, end, isAllDay) => {
        const matchingGoogleUser = googleUsers.find(
            user => user.isValid && user.syncedGoogleEmail === event?.extendedProps?.googleCalendarEmail
        );
        if (!matchingGoogleUser?._id) throw new Error("No valid Google account for this event");

        const req = {
            eventId: event.extendedProps.googleEventId,
            summary: event.titlePlain,
            start: isAllDay
                ? { date: moment(start).format('YYYY-MM-DD'), timeZone: tz }
                : { dateTime: moment(start).toISOString(), timeZone: tz },
            end: isAllDay
                ? { date: moment(end).format('YYYY-MM-DD'), timeZone: tz }
                : { dateTime: moment(end).toISOString(), timeZone: tz },
            editScope: 'thisEvent',
            ...(event.extendedProps.recurringEventId && {
                recurringEventId: event.extendedProps.recurringEventId,
                instanceDate: moment(event.start).format('YYYY-MM-DD'),
            }),
            googleUserId: matchingGoogleUser._id,
        };

        await axios.patch(`/api/google-calendar/events/move/${req.eventId}`, req, {
            headers: { Authorization: `Bearer ${token}` },
        });
    };

    const updateMicrosoftTimes = async (event, start, end, isAllDay) => {
        const toDT = (d) => moment(d).format('YYYY-MM-DD[T]HH:mm:ss');
        const matchingMicrosoftUser = microsoftUsers.find(
            user => user.isValid && user.syncedMicrosoftEmail === event?.extendedProps?.microsoftCalendarEmail
        );
        if (!matchingMicrosoftUser?._id) throw new Error("No valid Microsoft account for this event");
        const req = {
            eventId: event.extendedProps.microsoftEventId,
            subject: event.titlePlain,
            start: isAllDay
                ? { dateTime: `${moment(start).format('YYYY-MM-DD')}T00:00:00`, timeZone: tz }
                : { dateTime: toDT(start), timeZone: tz },
            end: isAllDay
                ? { dateTime: `${moment(end).format('YYYY-MM-DD')}T00:00:00`, timeZone: tz }
                : { dateTime: toDT(end), timeZone: tz },
            editScope: 'thisEvent',
            ...(event.extendedProps.recurrence && {
                recurringEventId: event.extendedProps.recurringEventId || event.extendedProps.microsoftEventId,
                instanceDate: moment(event.start).format('YYYY-MM-DD'),
            }),
            microsoftUserId: matchingMicrosoftUser._id,
        };

        await axios.patch(`/api/microsoft-calendar/events/move/${req.eventId}`, req, {
            headers: { Authorization: `Bearer ${token}` },
        });
    };

    const updateLocalTimes = async (event, start, end, isAllDay) => {
        const id = event.extendedProps.localEventId || event._id;
        const req = {
            event_name: event.titlePlain,
            event_date: moment(start).format(CALENDAR_CONFIG.DATE_FORMAT),
            end_date: moment(end).isSame(moment(start), 'day') ? null : moment(end).format(CALENDAR_CONFIG.DATE_FORMAT),
            start_date_time: !isAllDay ? moment(start).toISOString() : null,
            end_date_time: !isAllDay ? moment(end).toISOString() : null,
            repeat_type: event.extendedProps?.repeatType || 'does_not_repeat',
            update_scope: 'this',
            timeZone: tz,
        };

        await axios.patch(`/api/events/move/${id}`, req, {
            headers: { Authorization: `Bearer ${token}` },
        });
    };

    // OLD CODE - Commented out isSelectionMode prop
    const EventRenderer = useCallback((props) => {
        return <Event
            {...props}
            view={view}
            // isSelectionMode={isSelectionMode}
            selectedEventsForImport={selectedEventsForImport}
            setSelectedEventsForImport={setSelectedEventsForImport}
            eventKey={eventKey}
        />;
    }, [view, selectedEventsForImport]);

    const components = useMemo(() => ({
        toolbar: CustomToolbar,
        timeSlotsWrapper: CustomTimeSlotsWrapper,
        week: { header: CustomDayHeader },
        month: { header: CustomMonthHeader },
        event: EventRenderer,
    }), [CustomToolbar, EventRenderer]);

    const formats = useMemo(() => ({
        timeGutterFormat: (date) => moment(date).format("hh:mm A"),
    }), []);

    const eventPropGetter = useCallback((event) => ({
        style: {
            backgroundColor: event.backgroundColor,
            color: event.textColor,
            borderRadius: "4px",
            padding: "6px 16px 8px 6px",
            border: `1px solid ${event.fullBorderColor}`,
            borderLeft: `4px solid ${event.borderColor}`,
            transition: "transform 0.2s",
            fontWeight: "600",
        },
    }), []);

    // Precompute all holiday days (YYYY-MM-DD) for quick O(1) lookup
    const holidayIndex = useMemo(() => {
        const set = new Set();
        if (!Array.isArray(filteredEvents)) return set;

        for (const ev of filteredEvents) {
            if (ev?.eventType !== "Holiday" || ev?.appliesToUser === false) continue;

            const start = moment(startOfDayInTimezone(ev.start, tz));
            // end is stored as +1 day (exclusive). Make it inclusive by subtracting a day.
            const inclusiveEnd = moment(ev.end ? startOfDayInTimezone(ev.end, tz) : start.toDate())
                .clone()
                .subtract(1, "day")
                .startOf("day");

            for (let d = start.clone(); d.isSameOrBefore(inclusiveEnd, "day"); d.add(1, "day")) {
                set.add(d.format(CALENDAR_CONFIG.DATE_FORMAT));
            }
        }
        return set;
    }, [filteredEvents, tz]);

    const dayPropGetter = useCallback(
        (date) => {
            const key = moment.tz(date, tz).format(CALENDAR_CONFIG.DATE_FORMAT);
            const isHoliday = holidayIndex.has(key);
            return {
                className: isHoliday ? "holiday-date" : "",
                style: isHoliday ? { backgroundColor: CALENDAR_CONFIG.UI_COLORS.BG_HOLIDAY, zIndex: 1 } : {},
            };
        },
        [holidayIndex, tz]
    );

    useEffect(() => {
        const day = appSettings?.weekStartDay === 'Monday' ? 1 : 0;
        moment.updateLocale('en', { week: { dow: day } });
    }, [appSettings?.weekStartDay]);

    const handleSelectEvent = (event) => {
        setSelectedEvents(event.extendedProps.allEvents || [event.extendedProps]);
        setModalIsOpen(true);
        setExpandedEventIndex(null);
    };

    function formatTimeTo12Hour(timeString) {
        if (!timeString) return 'Invalid Time';

        // Try parsing HH:mm:ss or HH:mm format
        const timeParts = timeString.split(':');

        if (timeParts.length < 2) return 'Invalid Time';

        let hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        const ampm = hours >= 12 ? 'pm' : 'am';

        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'

        const formattedMinutes = isNaN(minutes) ? '00' : String(minutes).padStart(2, '0');

        return `${hours}:${formattedMinutes}${ampm}`;
    }

    const toggleCategory = useCallback((name) => {
        const currentCategories = Array.isArray(selectedCategories) ? selectedCategories : [];
        const newCategories = currentCategories.includes(name)
            ? currentCategories.filter((i) => i !== name)
            : [...currentCategories, name];
        setSelectedCategories(newCategories);
    }, [selectedCategories]);

    const handleClickOutsideDropdown = useCallback((event) => {
        const refs = {
            userDropdown: userDropdownRef.current,
            leaveUserDropdown: leaveUserDropdownRef.current,
            createDropdown: createDropdownRef.current,
        };

        try {
            // Close user dropdown if click is outside
            if (refs.userDropdown && !refs.userDropdown.contains(event.target)) {
                closeUserDropdown();
            }
            // Close leave user dropdown if click is outside
            if (refs.leaveUserDropdown && !refs.leaveUserDropdown.contains(event.target)) {
                closeLeaveUserDropdown();
            }
            // Close create dropdown if click is outside
            if (refs.createDropdown && !refs.createDropdown.contains(event.target)) {
                dispatchUI({ type: uiStateActions.SET_DROPDOWN, dropdownName: 'isOpen', isOpen: false });
            }
        } catch (err) {
            console.error('Error in handleClickOutsideDropdown:', err);
        }
    }, [dispatchUI, closeUserDropdown, closeLeaveUserDropdown]);

    useEffect(() => {
        dispatch(setHeaderText("Calendar"));
        if (typeof window === 'undefined' || !document) {
            return;
        }

        document.addEventListener('click', handleClickOutsideDropdown);

        return () => {
            document.removeEventListener('click', handleClickOutsideDropdown);
        };
    }, [dispatch, handleClickOutsideDropdown]);

    // Retry handler for all errors (must be defined before early returns)
    const handleRetryAll = useCallback(() => {
        if (leavesError) refetchLeaves();
        if (eventsError) refetchEvents();
        if (selectedGoogleEventsError) refetchSelectedGoogleEvents();
        if (selectedMicrosoftEventsError) refetchSelectedMicrosoftEvents();
    }, [leavesError, eventsError, selectedGoogleEventsError, selectedMicrosoftEventsError, refetchLeaves, refetchEvents, refetchSelectedGoogleEvents, refetchSelectedMicrosoftEvents]);

    // Show skeleton loaders for initial load, but allow partial rendering for subsequent loads
    const isInitialLoad = leavesLoading && eventsLoading && userProfileLoading;
    const hasPartialData = baseEvents.length > 0 || userProfiles.length > 0;

    // Handle errors consistently
    const errors = [
        leavesError && { source: 'Leaves', error: leavesError },
        eventsError && { source: 'Events', error: eventsError },
        selectedGoogleEventsError && { source: 'Google Calendar', error: selectedGoogleEventsError },
        selectedMicrosoftEventsError && { source: 'Microsoft Calendar', error: selectedMicrosoftEventsError },
    ].filter(Boolean);

    // Display errors if any exist (only show full-page error on initial load)
    if (errors.length > 0 && isInitialLoad && !hasPartialData) {
        return (
            <MultipleErrorsDisplay
                errors={errors.map((e) => e.error)}
                onRetry={handleRetryAll}
                context="loading calendar data"
            />
        );
    }

    // Show skeleton loader only on initial load
    if (isInitialLoad && !hasPartialData) {
        return (
            <div role="main" aria-label="Calendar">
                <FilterBarSkeleton />
                <CalendarSkeleton />
            </div>
        );
    }

    return (
        <>
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                `}
            </style>
            <div className='user-outer-overview report-top-button eport-top-button-new'>
                <div className='button-inner-report button-inner-report-new'>
                    <div className="dropdown-container-cal" ref={createDropdownRef}>
                        {/* disabled={isSelectionMode} - OLD CODE - Commented out */}
                        <button
                            className="dropdown-button-cal"
                            onClick={toggleDropdown}
                            style={{
                                opacity: 1, // OLD CODE: was isSelectionMode ? 0.5 : 1
                                cursor: 'pointer' // OLD CODE: was isSelectionMode ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <span className="icon-export">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M7.75 0V15.5" stroke="white" strokeWidth="1.5" />
                                    <path d="M15.5 7.75L-4.17233e-07 7.75" stroke="white" strokeWidth="1.5" />
                                </svg>
                            </span>
                            Create
                            <span className="icon-export icon-export-arrow">
                                <img src={ArrowIcon} alt="Arrow" />
                            </span>
                        </button>
                        {/* {isOpen && !isSelectionMode && ( */}
                        {isOpen && (
                            <div className="dropdown-menu-cal">
                                <div className="dropdown-item-cal"
                                    onClick={() => {
                                        handleOpenCreateEventModal();
                                        setIsOpen(false);
                                    }}
                                >
                                    Event
                                </div>
                                {(user && (user.userType === SUPER_ADMIN || user.userType === ADMIN)) && (
                                    <div className="dropdown-item-cal"
                                        onClick={() => {
                                            handleOpenCreateHolidayModal();
                                            setIsOpen(false);
                                        }}
                                    >
                                        Holiday
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* {!isSelectionMode && ( */}

                    <div className="button-inner-report" style={{ marginRight: '0px' }}>
                        <button
                            className="dropdown-button-cal"
                            onClick={() => {
                                setSelectedUsers(user?._id ? [user._id] : []);
                                setIsUserDropdownOpen(false);
                                // Set week view if not already in week or day view
                                // if (view !== 'week' && view !== 'day') {
                                //   dispatchUI({ type: uiStateActions.SET_VIEW, view: 'week' });
                                // }
                                // Get current view's date range (week/day/month) and open modal
                                const currentViewRange = getCurrentViewDateRange();
                                setIsImportDateRangeModalOpen(true, currentViewRange);
                            }}
                        >
                            <span className="icon-export">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="21"
                                    height="23"
                                    viewBox="0 0 21 23"
                                    fill="white"
                                >
                                    <path d="M7.88008 1.59379e-05C7.50837 0.00134006 7.20755 0.294417 7.20621 0.656569C7.20553 0.83136 7.27598 0.999304 7.40237 1.12333C7.52877 1.24736 7.70069 1.31754 7.88009 1.3182H9.82242V2.58363C4.34897 2.92702 0 7.35611 0 12.7718C0 18.4111 4.71168 23 10.502 23C16.2924 23 21 18.4111 21 12.7718C21 7.35419 16.6525 2.92487 11.1776 2.58363V1.3182H13.1199C13.3002 1.31886 13.4733 1.24934 13.6008 1.12509C13.7283 1.00085 13.7997 0.832236 13.799 0.656564C13.7983 0.481773 13.7263 0.31427 13.599 0.191123C13.4717 0.0679763 13.2993 -0.000656286 13.1199 4.73086e-06L7.88008 1.59379e-05ZM10.5015 3.86855C15.5609 3.86855 19.6466 7.84544 19.6466 12.7719C19.6466 17.6984 15.561 21.6821 10.5015 21.6821C5.44196 21.6821 1.35289 17.6988 1.35289 12.7719C1.35289 7.84544 5.44231 3.86855 10.5015 3.86855ZM10.5015 6.37386C6.88114 6.37386 3.93041 9.24594 3.93041 12.7722C3.93041 16.2984 6.88097 19.1716 10.5015 19.1716C14.122 19.1716 17.0697 16.2982 17.0697 12.7722C17.0697 9.24594 14.1216 6.37386 10.5015 6.37386ZM10.5015 7.6934C13.3909 7.6934 15.7153 9.95882 15.7153 12.7722C15.7153 15.5856 13.3909 17.8521 10.5015 17.8521C7.61212 17.8521 5.2836 15.5856 5.2836 12.7722C5.2836 9.95882 7.61212 7.6934 10.5015 7.6934ZM10.5015 9.23052C10.3221 9.22985 10.1497 9.29849 10.0224 9.42163C9.89491 9.54478 9.8231 9.71228 9.82242 9.88707V13.4106C9.82174 13.5863 9.8931 13.7549 10.0206 13.8792C10.1479 14.0034 10.3212 14.0729 10.5015 14.0723H14.118C14.2983 14.0729 14.4713 14.0034 14.5989 13.8792C14.7264 13.7549 14.7977 13.5863 14.7971 13.4106C14.7968 13.2356 14.725 13.0679 14.5975 12.9443C14.4702 12.8209 14.2976 12.7521 14.118 12.7528H11.1767V9.88705C11.1751 9.52445 10.8737 9.23119 10.5015 9.23052Z" />
                                </svg>
                            </span>
                            Add events to time tracker
                        </button>
                    </div>

                    {/* )} */}
                    {/* OLD CODE - Commented out as we're now using date range modal directly */}
                    {/* {isSelectionMode && (
                        <>
                            <div className="button-inner-report" style={{ marginLeft: '0px' }}>
                                <button
                                    className={`dropdown-button-cal ${isImportLoading ? 'button-cal-active' : ''}`}
                                    onClick={() => {
                                        if (selectAllEvents) {
                                            // If we already have a date range from previous use, reuse it;
                                            // otherwise initialize from the current view's date range
                                            const rangeToOpenWith =
                                                importDateRange && importDateRange.startDate && importDateRange.endDate
                                                    ? importDateRange
                                                    : getCurrentViewDateRange();
                                            setIsImportDateRangeModalOpen(true, rangeToOpenWith);
                                        } else {
                                            handleImportSelectedEvents();
                                        }
                                    }}
                                    disabled={(!selectedEventsForImport || selectedEventsForImport.length === 0) || isImportLoading}
                                    style={{
                                        opacity: ((!selectedEventsForImport || selectedEventsForImport.length === 0) || isImportLoading) ? 1 : 1,
                                        cursor: ((!selectedEventsForImport || selectedEventsForImport.length === 0) || isImportLoading) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {isImportLoading ? (
                                        <div
                                            style={{
                                                width: "80px",
                                                height: "20px",
                                                borderRadius: "4px",
                                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                                animation: "pulse 1.5s ease-in-out infinite",
                                            }}
                                        />
                                    ) : (
                                        <>
                                            <span className="icon-export">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="21"
                                                    height="23"
                                                    viewBox="0 0 21 23"
                                                    fill="white"
                                                >
                                                    <path d="M7.88008 1.59379e-05C7.50837 0.00134006 7.20755 0.294417 7.20621 0.656569C7.20553 0.83136 7.27598 0.999304 7.40237 1.12333C7.52877 1.24736 7.70069 1.31754 7.88009 1.3182H9.82242V2.58363C4.34897 2.92702 0 7.35611 0 12.7718C0 18.4111 4.71168 23 10.502 23C16.2924 23 21 18.4111 21 12.7718C21 7.35419 16.6525 2.92487 11.1776 2.58363V1.3182H13.1199C13.3002 1.31886 13.4733 1.24934 13.6008 1.12509C13.7283 1.00085 13.7997 0.832236 13.799 0.656564C13.7983 0.481773 13.7263 0.31427 13.599 0.191123C13.4717 0.0679763 13.2993 -0.000656286 13.1199 4.73086e-06L7.88008 1.59379e-05ZM10.5015 3.86855C15.5609 3.86855 19.6466 7.84544 19.6466 12.7719C19.6466 17.6984 15.561 21.6821 10.5015 21.6821C5.44196 21.6821 1.35289 17.6988 1.35289 12.7719C1.35289 7.84544 5.44231 3.86855 10.5015 3.86855ZM10.5015 6.37386C6.88114 6.37386 3.93041 9.24594 3.93041 12.7722C3.93041 16.2984 6.88097 19.1716 10.5015 19.1716C14.122 19.1716 17.0697 16.2982 17.0697 12.7722C17.0697 9.24594 14.1216 6.37386 10.5015 6.37386ZM10.5015 7.6934C13.3909 7.6934 15.7153 9.95882 15.7153 12.7722C15.7153 15.5856 13.3909 17.8521 10.5015 17.8521C7.61212 17.8521 5.2836 15.5856 5.2836 12.7722C5.2836 9.95882 7.61212 7.6934 10.5015 7.6934ZM10.5015 9.23052C10.3221 9.22985 10.1497 9.29849 10.0224 9.42163C9.89491 9.54478 9.8231 9.71228 9.82242 9.88707V13.4106C9.82174 13.5863 9.8931 13.7549 10.0206 13.8792C10.1479 14.0034 10.3212 14.0729 10.5015 14.0723H14.118C14.2983 14.0729 14.4713 14.0034 14.5989 13.8792C14.7264 13.7549 14.7977 13.5863 14.7971 13.4106C14.7968 13.2356 14.725 13.0679 14.5975 12.9443C14.4702 12.8209 14.2976 12.7521 14.118 12.7528H11.1767V9.88705C11.1751 9.52445 10.8737 9.23119 10.5015 9.23052Z" />
                                                </svg>
                                            </span>
                                            Import selected events ({selectedEventsForImport?.length || 0})
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="button-inner-report button--select" style={{ marginLeft: '0px', display: 'flex', alignItems: 'center' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginRight: '0px' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectAllEvents}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                // Select all events in current view only
                                                const eventsInView = getEventsInCurrentView();

                                                // Check if there are no events in the current view
                                                if (!eventsInView || eventsInView.length === 0) {
                                                    toast.error('No events available in the current view for selection', { position: "bottom-right", autoClose: 2000 });
                                                    setSelectAllEvents(false);
                                                    return;
                                                }

                                                // Only select unsynced events that have ended (end time before current time)
                                                const selectableEventsInView = eventsInView.filter(ev => {
                                                    if (isEventSynced(ev)) return false;
                                                    // Check if event end time is before current time
                                                    if (!ev.end) return false;
                                                    return moment(ev.end).isBefore(moment());
                                                });

                                                // Check if all events are already synced or haven't ended yet
                                                if (selectableEventsInView.length === 0) {
                                                    const unsyncedEvents = eventsInView.filter(ev => !isEventSynced(ev));
                                                    if (unsyncedEvents.length === 0) {
                                                        toast.error('All events in the current view are already synced to time tracker', { position: "bottom-right", autoClose: 2000 });
                                                    } else {
                                                        toast.error('No events available for selection. Only events that have ended can be selected.', { position: "bottom-right", autoClose: 2000 });
                                                    }
                                                    setSelectAllEvents(false);
                                                    return;
                                                }

                                                setSelectedEventsForImport(selectableEventsInView);
                                                setSelectAllEvents(true);
                                            } else {
                                                setSelectedEventsForImport([]);
                                                setSelectAllEvents(false);
                                            }
                                        }}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <span>Select All Events</span>
                                </label>
                            </div>
                        </>
                    )} */}
                </div>
                {/* OLD CODE - Commented out cancel button section */}
                {/* {isSelectionMode ? (
                    <div className="search-sec">
                        <button
                            className="cancelbtn-link"
                            onClick={() => {
                                if (!isImportLoading) {
                                    setIsSelectionMode(false);
                                    setSelectedEventsForImport([]);
                                    setSelectAllEvents(false);
                                }
                            }}
                            disabled={isImportLoading}
                            style={{
                                padding: '12px 15px 15px 16px',                        
                                borderRadius: '4px',
                                cursor: isImportLoading ? 'not-allowed' : 'pointer',
                                opacity: isImportLoading ? 0.5 : 1
                            }}
                        >
                            <img src={cancelImage} alt="cancel" /> Cancel
                        </button>
                    </div>
                ) : ( */}
                <div className="search-sec">
                    <div className="search-container">
                        <input
                            type="text"
                            onChange={handleFilterChange}
                            value={filt}
                            className="search-input"
                            placeholder={global.config.locate(appSettings && appSettings.language, 'Search') || "Search"}
                        />
                    </div>
                </div>
                {/* )} */}
            </div>
            <div className="calender-filter-outer">
                {(user && (user.userType === SUPER_ADMIN || user.userType === ADMIN)) && (
                    <div className="filter-inner invoice-status">
                        <label htmlFor="user">User Visibility</label>
                        <div className="custom-dropdown1" ref={userDropdownRef}>
                            <div className="drop-down-inner">
                                <div
                                    className="selected-options1"
                                    onClick={() => {
                                        if (isSelectionMode) return;
                                        if (!isUserDropdownOpen) openUserDropdown();
                                        else closeUserDropdown();
                                    }}
                                    style={{
                                        cursor: isSelectionMode ? 'not-allowed' : 'pointer',
                                        opacity: isSelectionMode ? 0.6 : 1,
                                        pointerEvents: isSelectionMode ? 'none' : 'auto'
                                    }}
                                >
                                    {isUserLoading ? (
                                        <div className="selected-option1">
                                            <div
                                                style={{
                                                    width: "120px",
                                                    height: "20px",
                                                    borderRadius: "4px",
                                                    backgroundColor: "#e5e7eb",
                                                    animation: "pulse 1.5s ease-in-out infinite",
                                                }}
                                            />
                                        </div>
                                    ) : selectedUsers.length === 0 ? (
                                        <div className="placeholder">Select</div>
                                    ) : (
                                        <div className="selected-option1" key={selectedUsers[0]}>
                                            {selectedUsers.length === mergedUsersForAdmin.length ? (
                                                `All`
                                            ) : (
                                                <>
                                                    <span>
                                                        {mergedUsersForAdmin.find((u) => u.userId?.toString() === selectedUsers[0]?.toString())?.username || 'Select User'}
                                                    </span>
                                                    {selectedUsers.length > 1 && selectedUsers.length !== mergedUsersForAdmin.length && (
                                                        <div className="selected-option-count">
                                                            {selectedUsers.length - 1}+
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* {isUserDropdownOpen && !isSelectionMode && ( */}
                                {isUserDropdownOpen && (
                                    <div className="dropdown-list1">
                                        <input
                                            type="text"
                                            placeholder="Search users"
                                            value={googleMicrosoftUserSearchTerm}
                                            onChange={(e) => setGoogleMicrosoftUserSearchTerm(e.target.value)}
                                            className="search-bar"
                                        />
                                        {mergedUsersForAdmin
                                            .filter((u) =>
                                                u.username?.toLowerCase().includes(googleMicrosoftUserSearchTerm.toLowerCase())
                                            )
                                            .length > 0 && (
                                                <>
                                                    {googleMicrosoftUserSearchTerm === '' && (
                                                        <label className="checkbox-label1" key="selectAll">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectAllUsers}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setSelectAllUsers(checked);
                                                                    if (checked) {
                                                                        setSelectedUsers(mergedUsersForAdmin.map((u) => u.userId).filter(Boolean));
                                                                    } else {
                                                                        setSelectedUsers([]);
                                                                    }
                                                                }}
                                                            />
                                                            All Users
                                                        </label>
                                                    )}
                                                    {mergedUsersForAdmin
                                                        .filter((u) =>
                                                            u.username?.toLowerCase().includes(googleMicrosoftUserSearchTerm.toLowerCase())
                                                        )
                                                        .sort((a, b) => (a.username || '').localeCompare(b.username || ''))
                                                        .map((u) => (
                                                            <label className="checkbox-label1" key={u.userId}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedUsers.map(id => id?.toString()).includes(u.userId?.toString())}
                                                                    onChange={() => {
                                                                        const userIdStr = u.userId?.toString();
                                                                        const currentSelectedStr = selectedUsers.map(id => id?.toString());
                                                                        setSelectedUsers(
                                                                            currentSelectedStr.includes(userIdStr)
                                                                                ? selectedUsers.filter((id) => id?.toString() !== userIdStr)
                                                                                : [...selectedUsers, u.userId]
                                                                        );
                                                                    }}
                                                                />
                                                                {u.username}
                                                            </label>
                                                        ))}
                                                </>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {/* Leave Filter Dropdown for Non-Admin Users */}
                {user && !(user.userType === SUPER_ADMIN || user.userType === ADMIN) && (
                    <div className="filter-inner invoice-status">
                        <label htmlFor="leaveUser">Time Off Filter</label>
                        <div className="custom-dropdown1" ref={leaveUserDropdownRef}>
                            <div className="drop-down-inner">
                                <div
                                    className="selected-options1"
                                    onClick={() => {
                                        if (isSelectionMode) return;
                                        if (!isLeaveUserDropdownOpen) openLeaveUserDropdown();
                                        else closeLeaveUserDropdown();
                                    }}
                                    style={{
                                        cursor: isSelectionMode ? 'not-allowed' : 'pointer',
                                        opacity: isSelectionMode ? 0.6 : 1,
                                        pointerEvents: isSelectionMode ? 'none' : 'auto'
                                    }}
                                >
                                    {isLeaveUserSuggestionsLoading ? (
                                        <div className="selected-option1">
                                            <div
                                                style={{
                                                    width: "120px",
                                                    height: "20px",
                                                    borderRadius: "4px",
                                                    backgroundColor: "#e5e7eb",
                                                    animation: "pulse 1.5s ease-in-out infinite",
                                                }}
                                            />
                                        </div>
                                    ) : leaveFilterUsers.length === 0 ? (
                                        <div className="placeholder">Select</div>
                                    ) : (
                                        <div className="selected-option1" key={leaveFilterUsers[0]}>
                                            {leaveFilterUsers.length === userSuggestions.length ? (
                                                `All`
                                            ) : (
                                                <>
                                                    <span>
                                                        {userSuggestions.find((u) => u._id.toString() === leaveFilterUsers[0].toString())?.first_name 
                                                            ? `${userSuggestions.find((u) => u._id.toString() === leaveFilterUsers[0].toString()).first_name} ${userSuggestions.find((u) => u._id.toString() === leaveFilterUsers[0].toString()).last_name || ''}`.trim()
                                                            : userSuggestions.find((u) => u._id.toString() === leaveFilterUsers[0].toString())?.company_first_name
                                                                ? `${userSuggestions.find((u) => u._id.toString() === leaveFilterUsers[0].toString()).company_first_name} ${userSuggestions.find((u) => u._id.toString() === leaveFilterUsers[0].toString()).company_last_name || ''}`.trim()
                                                                : 'Select User'}
                                                    </span>
                                                    {leaveFilterUsers.length > 1 && leaveFilterUsers.length !== userSuggestions.length && (
                                                        <div className="selected-option-count">
                                                            {leaveFilterUsers.length - 1}+
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {isLeaveUserDropdownOpen && (
                                    <div className="dropdown-list1">
                                        <input
                                            type="text"
                                            placeholder="Search users"
                                            value={leaveUserSearchTerm}
                                            onChange={(e) => setLeaveUserSearchTerm(e.target.value)}
                                            className="search-bar"
                                        />
                                        {userSuggestions
                                            .filter((u) => {
                                                const fullName = getUserDisplayName(u);
                                                return fullName.toLowerCase().includes(leaveUserSearchTerm.toLowerCase());
                                            })
                                            .length > 0 && (
                                                <>
                                                    {leaveUserSearchTerm === '' && (
                                                        <label className="checkbox-label1" key="selectAll">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectAllLeaveUsers}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setLeaveFilterUsers(userSuggestions.map((u) => u._id).filter(Boolean));
                                                                    } else {
                                                                        setLeaveFilterUsers([]);
                                                                    }
                                                                }}
                                                            />
                                                            All Users
                                                        </label>
                                                    )}
                                                    {userSuggestions
                                                        .filter((u) => {
                                                            const fullName = getUserDisplayName(u);
                                                            return fullName.toLowerCase().includes(leaveUserSearchTerm.toLowerCase());
                                                        })
                                                        .sort((a, b) => {
                                                            const nameA = getUserDisplayName(a);
                                                            const nameB = getUserDisplayName(b);
                                                            return nameA.localeCompare(nameB);
                                                        })
                                                        .map((u) => {
                                                            const fullName = getUserDisplayName(u);
                                                            return (
                                                                <label className="checkbox-label1" key={u._id}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={leaveFilterUsers.map(id => id?.toString()).includes(u._id?.toString())}
                                                                        onChange={() => {
                                                                            const userIdStr = u._id?.toString();
                                                                            const currentFilterStr = leaveFilterUsers.map(id => id?.toString());
                                                                            setLeaveFilterUsers(
                                                                                currentFilterStr.includes(userIdStr)
                                                                                    ? leaveFilterUsers.filter((id) => id?.toString() !== userIdStr)
                                                                                    : [...leaveFilterUsers, u._id]
                                                                            );
                                                                        }}
                                                                    />
                                                                    {fullName}
                                                                </label>
                                                            );
                                                        })}
                                                </>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div className="filter-inner filter-sec-category-calendar">
                    <div className="category-section">
                        <label>Categories</label>
                        <div className="category-box">
                            {CATEGORYLIST.map(({ name, color }) => {
                                const isChecked = Array.isArray(selectedCategories) && selectedCategories.includes(name);
                                return (
                                    <label
                                        key={name}
                                        className="category-pill"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleCategory(name)}
                                            style={{ backgroundColor: isChecked ? color : '#f1f1f1' }}
                                        />
                                        <span className="category-label">{name}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            <div className="calendar-container p-4" style={{ position: 'relative' }}>
                {isUpdating && (
                    <div
                        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/10 cursor-progress"
                        style={{ pointerEvents: 'auto' }}
                    >
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-300 border-t-transparent" />
                    </div>
                )}
                {/* Show skeleton loader when calendar events are loading due to user selection change */}
                {(selectedGoogleEventsLoading || selectedMicrosoftEventsLoading) ? (
                    <CalendarSkeleton />
                ) : (
                    // OLD CODE: selectable={!isSelectionMode} - now always true
                    // OLD CODE: onSelectSlot={isSelectionMode ? undefined : handleSelectSlot} - now always handleSelectSlot
                    <DnDCalendar
                        localizer={localizer}
                        events={groupedEvents}
                        startAccessor="start"
                        endAccessor="end"
                        views={["month", "week", "day"]}
                        step={60}
                        timeslots={1}
                        selectable={true}
                        draggableAccessor={canDrag}
                        resizable
                        resizableAccessor={canResize}
                        onEventDrop={onEventDrop}
                        onEventResize={onEventResize}
                        onSelectSlot={handleSelectSlot}
                        onSelectEvent={handleSelectEvent}
                        view={view}
                        onView={handleViewChange}
                        date={currentDate}
                        onNavigate={handleNavigate}
                        components={components}
                        tooltipAccessor={() => ''}
                        formats={formats}
                        style={{ minHeight: "785px", height: "max-content" }}
                        eventPropGetter={eventPropGetter}
                        dayPropGetter={dayPropGetter}
                    />
                )}
                <ViewCalendarEventModal
                    modalIsOpen={modalIsOpen}
                    setModalIsOpen={setModalIsOpen}
                    selectedEvents={selectedEvents}
                    setSelectedEvents={setSelectedEvents}
                    expandedEventIndex={expandedEventIndex}
                    setExpandedEventIndex={setExpandedEventIndex}
                    isSubmitLoading={isSubmitLoading}
                    setIsSubmitLoading={setIsSubmitLoading}
                    googleUsers={googleUsers}
                    microsoftUsers={microsoftUsers}
                    user={user}
                    getEventTypeLabel={getEventTypeLabel}
                    dummyImage={dummyImage}
                    STATUS_COLORS={STATUS_COLORS}
                    selectedEventForEdit={selectedEventForEdit}
                    setSelectedEventForEdit={setSelectedEventForEdit}
                    eventName={eventName}
                    setEventName={setEventName}
                    eventDate={eventDate}
                    setEventDate={setEventDate}
                    eventDescription={eventDescription}
                    setEventDescription={setEventDescription}
                    eventLocation={eventLocation}
                    setEventLocation={setEventLocation}
                    eventOrganizer={eventOrganizer}
                    setEventOrganizer={setEventOrganizer}
                    externalAttendees={externalAttendees}
                    setExternalAttendees={setExternalAttendees}
                    eventBannerFile={eventBannerFile}
                    setEventBannerFile={setEventBannerFile}
                    repeatType={repeatType}
                    setRepeatType={setRepeatType}
                    eventStartTime={eventStartTime}
                    setEventStartTime={setEventStartTime}
                    eventEndTime={eventEndTime}
                    setEventEndTime={setEventEndTime}
                    formatTimeTo12Hour={formatTimeTo12Hour}
                    userProfiles={userProfiles}
                    userProfileLoading={userProfileLoading}
                    refetchLeaves={refetchLeaves}
                    refetchEvents={refetchEvents}
                    refetchSelectedGoogleEvents={refetchSelectedGoogleEvents}
                    refetchSelectedMicrosoftEvents={refetchSelectedMicrosoftEvents}
                    refetchHolidays={refetchHolidays}
                    isEventSynced={isEventSynced}
                />
                <CreateEventModal
                    createEventModalOpen={createEventModalOpen}
                    setCreateEventModalOpen={handleSetCreateEventModalOpen}
                    isSubmitLoading={isSubmitLoading}
                    setIsSubmitLoading={setIsSubmitLoading}
                    isGoogleSyncVisible={isGoogleSyncVisible}
                    isMicrosoftSyncVisible={isMicrosoftSyncVisible}
                    googleUsers={googleUsers}
                    microsoftUsers={microsoftUsers}
                    setIsMicrosoftTokenInvalidModalOpen={setIsMicrosoftTokenInvalidModalOpen}
                    eventName={eventName}
                    setEventName={setEventName}
                    eventNameIsValid={eventNameIsValid}
                    setEventNameIsValid={setEventNameIsValid}
                    repeatType={repeatType}
                    setRepeatType={setRepeatType}
                    repeatTypeIsValid={repeatTypeIsValid}
                    setRepeatTypeIsValid={setRepeatTypeIsValid}
                    repeatTypeDropdownOpen={repeatTypeDropdownOpen}
                    setRepeatTypeDropdownOpen={setRepeatTypeDropdownOpen}
                    eventDate={eventDate}
                    setEventDate={setEventDate}
                    eventDateIsValid={eventDateIsValid}
                    setEventDateIsValid={setEventDateIsValid}
                    appSettings={appSettings}
                    isSlotSelected={isSlotSelected}
                    setIsSlotSelected={setIsSlotSelected}
                    eventStartTime={eventStartTime}
                    setEventStartTime={setEventStartTime}
                    startTimeIsValid={startTimeIsValid}
                    setStartTimeIsValid={setStartTimeIsValid}
                    eventStartTimeIsValid={eventStartTimeIsValid}
                    setEventStartTimeIsValid={setEventStartTimeIsValid}
                    eventEndTime={eventEndTime}
                    setEventEndTime={setEventEndTime}
                    endTimeIsValid={endTimeIsValid}
                    setStartTime={setStartTime}
                    setEndTime={setEndTime}
                    setEndTimeIsValid={setEndTimeIsValid}
                    eventEndTimeIsValid={eventEndTimeIsValid}
                    setEventEndTimeIsValid={setEventEndTimeIsValid}
                    eventDescription={eventDescription}
                    setEventDescription={setEventDescription}
                    eventLocation={eventLocation}
                    setEventLocation={setEventLocation}
                    eventBannerFile={eventBannerFile}
                    setEventBanner={setEventBanner}
                    endTimeString={endTimeString}
                    setEndTimeString={setEndTimeString}
                    startTimeString={startTimeString}
                    setStartTimeString={setStartTimeString}
                    user={user}
                    eventOrganizer={eventOrganizer}
                    setEventOrganizer={setEventOrganizer}
                    organizerDropdownOpen={organizerDropdownOpen}
                    setOrganizerDropdownOpen={setOrganizerDropdownOpen}
                    userProfiles={userProfiles}
                    userProfileLoading={userProfileLoading}
                    eventAttendees={eventAttendees}
                    setEventAttendees={setEventAttendees}
                    externalAttendees={externalAttendees}
                    setExternalAttendees={setExternalAttendees}
                    isVirtual={isVirtual}
                    setIsVirtual={setIsVirtual}
                    eventMeetingLink={eventMeetingLink}
                    setEventMeetingLink={setEventMeetingLink}
                    reminderMethod={reminderMethod}
                    setReminderMethod={setReminderMethod}
                    reminderMethodDropdownOpen={reminderMethodDropdownOpen}
                    setReminderMethodDropdownOpen={setReminderMethodDropdownOpen}
                    reminderMinutes={reminderMinutes}
                    setReminderMinutes={setReminderMinutes}
                    reminderMinutesIsValid={reminderMinutesIsValid}
                    setReminderMinutesIsValid={setReminderMinutesIsValid}
                    token={token}
                    setIsMicrosoftTokenChecking={setIsMicrosoftTokenChecking}
                    checkMicrosoftTokenValidity={checkMicrosoftTokenValidity}
                    checkMicrosoftCalendarEnabledStatus={checkMicrosoftCalendarEnabledStatus}
                    refetchEvents={refetchEvents}
                    refetchSelectedGoogleEvents={refetchSelectedGoogleEvents}
                    refetchSelectedMicrosoftEvents={refetchSelectedMicrosoftEvents}
                />
                <CreateHolidayModal
                    createHolidayModalOpen={createHolidayModalOpen}
                    setCreateHolidayModalOpen={setCreateHolidayModalOpen}
                    isSubmitLoading={isSubmitLoading}
                    setIsSubmitLoading={setIsSubmitLoading}
                    user={user}
                    token={token}
                    refetchHolidays={refetchHolidays}
                    userProfiles={userProfiles}
                />
                <GoogleTokenInvalidModal
                    isGoogleTokenInvalidModalOpen={isGoogleTokenInvalidModalOpen}
                    setIsGoogleTokenInvalidModalOpen={setIsGoogleTokenInvalidModalOpen}
                    user={user}
                    isGoogleTokenChecking={isGoogleTokenChecking}
                />
                <MicrosoftTokenInvalidModal
                    isMicrosoftTokenInvalidModalOpen={isMicrosoftTokenInvalidModalOpen}
                    setIsMicrosoftTokenInvalidModalOpen={setIsMicrosoftTokenInvalidModalOpen}
                    user={user}
                    isMicrosoftTokenChecking={isMicrosoftTokenChecking}
                />
                <ImportDateRangeModal
                    isOpen={isImportDateRangeModalOpen}
                    onClose={() => {
                        if (!isImportLoading) {
                            // When closing the modal via Cancel, reset the stored importDateRange
                            // so that next time we open, it initializes from the current view again
                            setIsImportDateRangeModalOpen(false, { startDate: null, endDate: null });
                        }
                    }}
                    onConfirm={handleConfirmImportWithDateRange}
                    dateRange={importDateRange}
                    currentViewDateRange={getCurrentViewDateRange()}
                    isImportLoading={isImportLoading}
                    onDateRangeChange={(range) => {
                        setIsImportDateRangeModalOpen(true, range);
                        // Update selected events based on new date range
                        if (range.startDate && range.endDate) {
                            const startDate = moment(range.startDate).startOf('day');
                            const endDate = moment(range.endDate).endOf('day');

                            const eventsInRange = filteredEvents.filter(event => {
                                const eventType = event.eventType || event.extendedProps?.eventType;
                                if (!['Event', 'GoogleEvent', 'MicrosoftEvent'].includes(eventType)) {
                                    return false;
                                }
                                // Only include events with start and end times (not all-day)
                                const isAllDay = event.allDay === true || !event.start || !event.end;
                                if (isAllDay) return false;

                                const eventStart = moment(event.start);
                                return eventStart.isSameOrAfter(startDate) && eventStart.isSameOrBefore(endDate);
                            });

                            setSelectedEventsForImport(eventsInRange);
                        }
                    }}
                />
            </div>
        </>
    );
};

export default Calender;